import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getProject,
  getClient,
  getShifts,
  getVenues,
  getVenueLocations,
  getTaskDefinitions,
  getShiftTasks,
  getShiftLineItems,
  createShiftLineItem,
  updateShiftLineItem,
  previewShiftRules,
  createShiftWithRules,
  createShiftWithRulesAutoApply,
  updateShift,
  updateShiftWithRules,
  deleteShiftTask,
  deleteShift,
  getShiftDeletionImpact,
  duplicateShift,
  duplicateShiftMultiDay,
  generateQuoteFromProject,
  recordShiftRuleAcceptances,
  extractErrorMessage,
  api,
  getTaskRuleConfigs,
  getTaskCategories, // EVENTSFLO-SHIFT-EDIT-TASK-DEFINITION-DROPDOWN-VISIBILITY-FIX-V1
  getStaffList,
  getClientContacts,
  // EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1
  downloadProjectPassportExport,
} from "../lib/api";
// EVENTSFLO-SHIFT-TIME-MATHS-SHARED-UTILITY-ROLLOUT-V1
import {
  calculateEndTime as shiftTimeMathEndTime,
  calculateDurationHours as shiftTimeMathDurationHours,
} from "../lib/shiftTimeMath";
import {
  StatusBadge,
  LoadingSpinner,
  EmptyState,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
// EVENTSFLO-CLIENT-SCOPED-VENUE-LOCATION-FILTERING-V1
import { filterVenuesForClient } from "../lib/venueFilters";
// EVENTSFLO-TAX-CLASS-DEFAULTS-AND-GENERATION-PREFLIGHT-V1
import { QuoteTaxPreflight } from "../components/financial/QuoteTaxPreflight";
import { RulePromptDialog } from "../components/shared/RulePromptDialog";
import { TravelAdvisorModal } from "../components/shared/TravelAdvisorModal";
import { ShiftLineItems } from "../components/shifts/ShiftLineItems";
import ShiftRuleStatusBadge from "../components/shifts/ShiftRuleStatusBadge";
import RuleResolutionModal from "../components/rules/RuleResolutionModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { DatePicker } from "../components/ui/date-picker";
import { TimePicker } from "../components/ui/time-picker";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Clock,
  Users,
  Calendar,
  FileText,
  Building2,
  Pencil,
  Trash2,
  ClipboardList,
  ListChecks,
  X,
  ChevronsRight,
  Moon,
  UserPlus,
  Copy,
  LayoutList,
  LayoutGrid,
  AlertCircle,
  MapPin,
  Info,
  TableProperties,
  Car,
  BookUser, // EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1
  DollarSign, // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
} from "lucide-react";
import { ShiftPlannerView } from "../components/planner/ShiftPlannerView";
import { ShiftDuplicationModal } from "../components/shifts/ShiftDuplicationModal";
import { Switch } from "../components/ui/switch";

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // EVENTSFLO-CREW-PLANNER-CONFIRMED-STATUS-AND-SHIFT-NAVIGATION-FIX-V1
  // Track a pending `?editShift=<id>` query param so that when the
  // user arrives from Crew Planner we can open the Edit Shift dialog
  // for that specific shift once the shifts list finishes loading.
  const pendingEditShiftIdRef = useRef(null);
  // EVENTSFLO-PROJECT-CREATE-NEXT-STEP-ADD-SHIFT-V1
  // Latch a pending "auto-open Add Shift" request that arrives via
  // `?addShift=true` (used right after Project creation from
  // ProjectsPage). Consumed exactly once after the shifts + venues
  // + taskDefinitions are all loaded so the Add Shift dialog opens
  // with the correct default inheritance.
  const pendingAddShiftRef = useRef(false);
  // EVENTSFLO-CREW-PLANNER-SHIFT-EDIT-RETURN-AND-CONFIRMATION-AUDIT-V1
  // Capture the caller's return URL (passed by Crew Planner via
  // `location.state.returnUrl`) so that closing the Edit Shift
  // dialog (via X / Cancel / Save / Discard) sends the user back
  // to Crew Planner instead of leaving them on the Project screen.
  // For any Edit Shift dialog opened from within Project Detail
  // (not via the ?editShift query param), this ref stays null and
  // the existing behaviour — stay on Project Detail — is preserved.
  const crewPlannerReturnUrlRef = useRef(null);
  // Latch: turns true while the Edit Shift dialog is open under the
  // Crew Planner deep-link. When the dialog transitions from open
  // → closed we consume the returnUrl exactly once.
  const shiftDialogWasOpenFromCrewPlannerRef = useRef(false);
  const { formatDate, formatCurrency, formatTime, formatDuration } = useFormatters();

  // Data state
  const [project, setProject] = useState(null);
  const [client, setClient] = useState(null);
  const [shifts, setShifts] = useState([]);
  // EVENTSFLO-SHIFT-RULE-STATUS-SHIELD-NON-BLOCKING-V1: bumped after a shift save
  // so every <ShiftRuleStatusBadge> re-fetches its read-only status.
  const [shiftBadgeRefreshKey, setShiftBadgeRefreshKey] = useState(0);
  // EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1
  const [passportExportConfirmOpen, setPassportExportConfirmOpen] = useState(false);
  const [passportExportLoading, setPassportExportLoading] = useState(false);
  // EVENTSFLO-SHIFT-RULE-SUPPRESSED-ITEM-LABEL-VISIBILITY-FIX-V1
  // Resolved rule-status for the currently-open shift edit modal.
  // Surfaced from <ShiftRuleStatusBadge onLoaded> so we can render
  // [Manual Override] badges next to the rule-label selectors.
  const [editingShiftRuleStatus, setEditingShiftRuleStatus] = useState(null);
  // New venue structure
  const [venues, setVenues] = useState([]);
  const [venueLocations, setVenueLocations] = useState([]);
  const [taskDefinitions, setTaskDefinitions] = useState([]);
  // EF-RULE-CONFIG-02: Task rule configs for dynamic filtering
  const [taskRuleConfigs, setTaskRuleConfigs] = useState([]);
  // EVENTSFLO-SHIFT-EDIT-TASK-DEFINITION-DROPDOWN-VISIBILITY-FIX-V1: drive
  // the primary Crew Role dropdown from the live ROLE category id rather
  // than a hardcoded UUID that pointed at a dead/legacy category.
  const [taskCategories, setTaskCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [deletionReason, setDeletionReason] = useState("");
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [editingShift, setEditingShift] = useState(null);  // For edit mode
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [shiftSubmitting, setShiftSubmitting] = useState(false);
  // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
  // Imperative ref into <ShiftLineItems/>. Used to detect unsaved
  // supplement edits and to inverse-undo them when the modal is
  // closed without Save.
  const shiftLineItemsRef = useRef(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [discardInProgress, setDiscardInProgress] = useState(false);
  
  // Validation state
  const [shiftValidationErrors, setShiftValidationErrors] = useState({});

  // Rule prompt state
  const [rulePromptOpen, setRulePromptOpen] = useState(false);
  const [pendingShift, setPendingShift] = useState(null);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [rulePrompts, setRulePrompts] = useState([]);
  const [ruleLoading, setRuleLoading] = useState(false);

  // Rule resolution modal state (mandatory compliance for rules that fire)
  const [resolutionModalOpen, setResolutionModalOpen] = useState(false);
  const [firedRules, setFiredRules] = useState([]);
  const [resolutionShiftId, setResolutionShiftId] = useState(null);
  const [resolutionShiftData, setResolutionShiftData] = useState({});

  // Rule auto-supplement visibility state
  const [ruleAddedSupplements, setRuleAddedSupplements] = useState([]);

  // Crew Leader shift offer state
  const [crewLeaderDialogOpen, setCrewLeaderDialogOpen] = useState(false);
  const [crewLeaderShiftData, setCrewLeaderShiftData] = useState(null);

  // EF-TRAVEL-ADVISOR-01: Travel Advisor state
  const [travelAdvisorOpen, setTravelAdvisorOpen] = useState(false);
  const [crewList, setCrewList] = useState([]);
  
  // PART A: OOT warning state
  const [ootOffWarningShown, setOotOffWarningShown] = useState(false);

  // View mode state: "table" (Edit Shift 1) or "planner" (Edit Shift 2)
  const [shiftViewMode, setShiftViewMode] = useState("table");

  // EF-DUPLICATION-PRECREATE-UX-ADJUSTMENT v1.1: Duplication state
  // Modal shown BEFORE entering Edit Shift, duplication executed AFTER save
  const [duplicationModalOpen, setDuplicationModalOpen] = useState(false);
  const [duplicationPlan, setDuplicationPlan] = useState(null); // { dates: [...] } - frontend only
  const [duplicationLoading, setDuplicationLoading] = useState(false);
  const [pendingShiftOpen, setPendingShiftOpen] = useState(false); // Flag to open form after modal

  // Shift form state
  const [shiftForm, setShiftForm] = useState({
    // New venue structure - both required
    venue_id: "",
    venue_location_id: "",
    name: "",
    date: "",
    start_time: "",
    // Duration is now first-class field - authoritative source
    duration: "",
    // end_time is derived from start_time + duration
    end_time: "",
    staff_required: 1,
    // EF-SHIFT-STAFFING-FIELD-PARAMETERIZATION: Primary role selection
    primary_role_id: "",
    status: "draft",
    notes: "",
    // EF-SHIFT-MEETING-PLACE-V1: Meeting place instructions
    meeting_place: "",
    out_of_town: false,
    // EVENTSFLO-SHIFT-ON-SITE-CONTACT-FROM-CLIENT-CONTACTS-V1
    on_site_contact_id: "",
    // EVENTSFLO-SHIFT-RULE-SUPPLEMENT-TASK-OVERRIDE-V1
    meal_supplement_task_definition_id: "",
    crew_chief_supplement_task_definition_id: "",
  });

  // EF-STABILIZATION-REGRESSION-ROUND-2: Ref to track current shiftForm for OOT toggle
  const shiftFormRef = useRef(shiftForm);
  useEffect(() => {
    shiftFormRef.current = shiftForm;
  }, [shiftForm]);

  // Inline tasks state - array of { task_definition_id, quantity, charge_rate, pay_rate }
  const [shiftTasks, setShiftTasks] = useState([]);

  // EVENTSFLO-SHIFT-ON-SITE-CONTACT-FROM-CLIENT-CONTACTS-V1
  // Client contacts loaded once we know the project's client_id; used by
  // the shift edit dialog's On-Site Contact dropdown.
  const [clientContacts, setClientContacts] = useState([]);

  // Quote form state
  // EF-QUOTE-TAX-FIELD-FORCE-REMOVAL-V2: tax_rate removed - backend uses tenant settings
  const [quoteForm, setQuoteForm] = useState({
    issue_date: new Date().toISOString().split("T")[0],
    valid_until: "",
    notes: "",
    // EVENTSFLO-PDF-VAT-LINE-LABEL-TOGGLE-V1 / -DEFAULT-OFF-V1
    // Whether the generated Quote PDF should render per-line
    // VAT/TVA labels (e.g. "VAT 0% Non-EU", "TVA 20%"). Default
    // OFF as of DEFAULT-OFF-V1. Disabling preserves totals + RC
    // notes. Existing quotes already saved with the flag are
    // respected on load.
    show_line_tax_labels_on_pdf: false,
  });

  // EVENTSFLO-TAX-CLASS-DEFAULTS-AND-GENERATION-PREFLIGHT-V1
  const [taxOverrides, setTaxOverrides] = useState({
    applyMatrix: true,
    includeRcNote: true,
    blockOnManualReview: true,
    overrideReason: "",
  });
  // Snapshot emitted from the preflight component so the parent can
  // decide whether to block the Generate Quote submit.
  const [preflightSummary, setPreflightSummary] = useState({
    loaded: false,
    aggregator_error: null,
    manual_review_count: 0,
    is_empty: false,
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectRes, shiftsRes, venuesRes, venueLocationsRes, taskDefsRes, ruleConfigsRes, taskCategoriesRes] = await Promise.all([
        getProject(id),
        getShifts({ project_id: id }),
        getVenues(),
        getVenueLocations(),
        getTaskDefinitions({ limit: 500, enabled_filter: "enabled" }), // EVENTSFLO-TASK-DEFINITIONS-DISABLE-HIDE-FROM-ASSIGNMENT-V1: Only enabled tasks for shift assignment
        getTaskRuleConfigs(), // EF-RULE-CONFIG-02: Load task rule configs
        getTaskCategories(),  // EVENTSFLO-SHIFT-EDIT-TASK-DEFINITION-DROPDOWN-VISIBILITY-FIX-V1
      ]);

      setProject(projectRes.data);
      setShifts(shiftsRes.data);
      // EVENTSFLO-CREW-PLANNER-CONFIRMED-STATUS-AND-SHIFT-NAVIGATION-FIX-V1
      // If we arrived with `?editShift=<id>` (from Crew Planner) and
      // that shift belongs to this project, open the Edit Shift
      // dialog directly instead of showing the shifts list.
      try {
        const params = new URLSearchParams(location.search || "");
        const editShiftId = params.get("editShift");
        if (editShiftId) {
          const target = (shiftsRes.data || []).find((s) => s.id === editShiftId);
          if (target) {
            pendingEditShiftIdRef.current = editShiftId;
            // EVENTSFLO-CREW-PLANNER-SHIFT-EDIT-RETURN-AND-CONFIRMATION-AUDIT-V1
            // Preserve the caller's return URL so we can send the
            // user back to Crew Planner when the dialog closes.
            const incomingReturnUrl = location.state?.returnUrl;
            if (typeof incomingReturnUrl === "string" && incomingReturnUrl.startsWith("/")) {
              crewPlannerReturnUrlRef.current = incomingReturnUrl;
            }
            // Clean the query param so a manual refresh doesn't
            // re-open the modal.
            navigate(location.pathname, { replace: true, state: location.state });
          }
        }
      } catch (_e) { /* non-fatal */ void _e; }
      // EVENTSFLO-PROJECT-CREATE-NEXT-STEP-ADD-SHIFT-V1
      // If we arrived with `?addShift=true` (e.g. right after
      // creating a new Project from ProjectsPage), latch a pending
      // add-shift flag so the shift-dialog useEffect fires once
      // reference data (venues / venue-locations / task defs) is
      // in state. We also strip the query param so a page refresh
      // doesn't repeatedly re-open the dialog.
      try {
        const params = new URLSearchParams(location.search || "");
        if (params.get("addShift") === "true") {
          pendingAddShiftRef.current = true;
          navigate(location.pathname, { replace: true, state: location.state });
        }
      } catch (_e) { /* non-fatal */ void _e; }
      setVenues(venuesRes.data);
      setVenueLocations(venueLocationsRes.data);
      setTaskDefinitions(taskDefsRes.data);
      setTaskRuleConfigs(ruleConfigsRes.data || []);
      setTaskCategories(taskCategoriesRes.data || []);

      // Load client data
      if (projectRes.data.client_id) {
        const clientRes = await getClient(projectRes.data.client_id);
        setClient(clientRes.data);
        // EVENTSFLO-SHIFT-ON-SITE-CONTACT-FROM-CLIENT-CONTACTS-V1
        try {
          const contactsRes = await getClientContacts(projectRes.data.client_id, { active: true });
          setClientContacts(Array.isArray(contactsRes.data) ? contactsRes.data : []);
        } catch {
          setClientContacts([]);
        }
      }
      
      // Return shifts count for callers that need immediate access
      return { shiftCount: shiftsRes.data?.length || 0 };
    } catch (err) {
      toast.error("Failed to load project data");
      navigate("/projects");
      return { shiftCount: 0 };
    } finally {
      setLoading(false);
    }
  };

  // Calculate summary stats
  const stats = {
    totalShifts: shifts.length,
    totalStaffRequired: shifts.reduce((acc, s) => acc + (s.staff_required || 0), 0),
    totalHours: shifts.reduce((acc, s) => acc + (s.duration_hours || 0), 0),
  };

  // Get venue name from venue_id
  const getVenueName = (venueId) => {
    const venue = venues.find((v) => v.id === venueId);
    return venue?.name || "Unknown";
  };
  
  // Get venue location name from venue_location_id
  const getVenueLocationName = (venueLocationId) => {
    const vl = venueLocations.find((v) => v.id === venueLocationId);
    return vl?.name || "Unknown";
  };
  
  // Get filtered venue locations for a specific venue (cascading dropdown)
  const getFilteredVenueLocations = (venueId) => {
    if (!venueId) return [];
    return venueLocations.filter((vl) => vl.venue_id === venueId);
  };

  const getTaskDefinitionById = (taskId) => {
    return taskDefinitions.find((t) => t.id === taskId);
  };

  // EF-SHIFT-MULTI-DAY-DUPLICATION v1.0: Check if project spans multiple days
  const isMultiDayProject = useMemo(() => {
    if (!project?.start_date || !project?.end_date) return false;
    const start = new Date(project.start_date);
    const end = new Date(project.end_date);
    return end > start;
  }, [project]);

  // EF-SHIFT-MULTI-DAY-DUPLICATION v1.0: Check if this is the first shift on the project
  const isFirstShiftOnProject = useMemo(() => {
    // First shift means no existing shifts before this one was created
    return shifts.length === 1;
  }, [shifts]);

  // EF-SHIFT-STAFFING-FIELD-PARAMETERIZATION: Get available staffing roles.
  // EVENTSFLO-TASK-DEFINITION-ROLE-CATALOGUE-NORMALISATION-V1:
  //   The 15 legacy STAFFING role rows that this tenant relies on (Crew 1st
  //   May double time, Crew No Show, Crew paye a temps double, General
  //   Production Crew, beMatrix / Chairs / Lighting / Rigging / Set Company /
  //   Staging / Truck Unloading / Video Crew, Marketing/Promotion iCrew,
  //   Pre-production, Production Manager) have been migrated to the live
  //   `ROLE` category. The Primary Crew Role dropdown therefore now has a
  //   single source of truth — the live ROLE category — and no longer needs
  //   the legacy STAFFING category union from V3.
  //
  //   We preserve the V1 backward-compatibility safety: the currently-
  //   selected `primary_role_id` is always included even if it falls outside
  //   the active ROLE category, so any shift that still references a non-
  //   migrated legacy row (activity items such as Load-In or Stage Rigging
  //   that are NOT crew roles) keeps its saved selection.
  const DEFAULT_CREW_ID = "44c2f5a2-fc7f-4cc3-b4c2-e40bdcfdb2cf";

  const roleCategoryId = useMemo(() => {
    const cat = (taskCategories || []).find((c) => (c?.key || "").toUpperCase() === "ROLE");
    return cat?.id || null;
  }, [taskCategories]);

  const staffingRoles = useMemo(() => {
    if (!roleCategoryId) return [];
    const list = taskDefinitions.filter(
      (t) => t.is_active !== false && t.category_id === roleCategoryId,
    );
    const selectedId = shiftForm?.primary_role_id;
    if (selectedId && !list.find((t) => t.id === selectedId)) {
      const found = taskDefinitions.find((t) => t.id === selectedId);
      if (found) list.push(found);
    }
    return list.sort((a, b) => {
      if (a.name === "Crew") return -1;
      if (b.name === "Crew") return 1;
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [taskDefinitions, roleCategoryId, shiftForm?.primary_role_id]);

  // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
  // Resolve task definitions eligible to be added as "Associated Costs /
  // Supplements" on the shift form. Matches the same live-category +
  // orphan-fallback logic used in ShiftLineItems.loadAvailableSupplements
  // so Create Shift and Edit Shift behave identically.
  const supplementTaskDefs = useMemo(() => {
    const liveCatIds = new Set((taskCategories || []).map((c) => c.id));
    const supplementCatIds = new Set(
      (taskCategories || [])
        .filter((c) => {
          const k = (c?.key || "").toUpperCase();
          return k === "SUPPLEMENT" || k === "ITEM";
        })
        .map((c) => c.id),
    );
    return (taskDefinitions || []).filter((t) => {
      if (t.is_active === false) return false;
      if (t.category_id && supplementCatIds.has(t.category_id)) return true;
      const tt = (t.task_type || "").toLowerCase();
      if (
        t.category_id
        && !liveCatIds.has(t.category_id)
        && (tt === "item" || tt === "supplement" || tt === "allowance")
      ) {
        return true;
      }
      return false;
    }).sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [taskDefinitions, taskCategories]);

  // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
  // Helper: classify a row already in shiftTasks as supplement vs crew.
  // Reads the corresponding task definition each time so callers can
  // visually separate the two lists without owning a second piece of
  // state. Robust to deleted/orphaned categories.
  const isSupplementShiftTask = useCallback((task) => {
    const explicit = (task?.type_snapshot || "").toLowerCase();
    if (explicit === "supplement") return true;
    if (explicit === "primary") return false;
    const td = taskDefinitions.find(
      (t) => t.id === task?.task_definition_id,
    );
    if (!td) return false;
    const liveCatIds = new Set((taskCategories || []).map((c) => c.id));
    const supplementCatIds = new Set(
      (taskCategories || [])
        .filter((c) => {
          const k = (c?.key || "").toUpperCase();
          return k === "SUPPLEMENT" || k === "ITEM";
        })
        .map((c) => c.id),
    );
    if (td.category_id && supplementCatIds.has(td.category_id)) return true;
    const tt = (td.task_type || "").toLowerCase();
    if (
      td.category_id
      && !liveCatIds.has(td.category_id)
      && (tt === "item" || tt === "supplement" || tt === "allowance")
    ) {
      return true;
    }
    return false;
  }, [taskDefinitions, taskCategories]);

  // EF-TASK-TYPE-REFORM-02 Phase 3: Dual-path filtering with validation
  // Performs BOTH string-based and ID-based filtering, logs discrepancies
  const getTaskDefinitionsForRule = useCallback((ruleKey) => {
    // Find the rule config
    const config = taskRuleConfigs.find(c => c.rule_key === ruleKey);
    
    if (!config) {
      console.warn(`[EF-TASK-TYPE-REFORM-04] No rule config found for ${ruleKey}`);
      return [];
    }
    
    // EF-TASK-TYPE-REFORM-04: Strictly ID-based filtering (no string fallback)
    const categoryId = config.category_id;
    const subcategoryId = config.subcategory_id;
    
    if (!categoryId || !subcategoryId) {
      console.warn(`[EF-TASK-TYPE-REFORM-04] Missing category/subcategory IDs for ${ruleKey}`);
      return [];
    }
    
    return taskDefinitions.filter(td =>
      td.category_id === categoryId &&
      td.subcategory_id === subcategoryId &&
      td.is_active === true
    );
  }, [taskDefinitions, taskRuleConfigs]);

  // EF-RULE-CONFIG-02: Get rule config by key
  const getRuleConfig = useCallback((ruleKey) => {
    const config = taskRuleConfigs.find(c => c.rule_key === ruleKey);
    if (!config) {
      return { allow_multiple: false };
    }
    return config;
  }, [taskRuleConfigs]);

  // Shift duration / end-time math — EVENTSFLO-SHIFT-TIME-MATHS-SHARED-UTILITY-ROLLOUT-V1
  // Migrated to the shared `lib/shiftTimeMath` utility (decimal-hour aware,
  // overnight-aware). Wrapped in useCallback to keep referential stability
  // for downstream `useCallback` deps that already capture these handles.
  const calculateDuration = useCallback((startTime, endTime) => {
    const hours = shiftTimeMathDurationHours(startTime, endTime);
    if (hours == null) return 0;
    return hours.toFixed(1);
  }, []);

  const calculateEndTime = useCallback((startTime, durationHours) => {
    return shiftTimeMathEndTime(startTime, durationHours);
  }, []);

  // Handle duration change - DURATION IS FIRST-CLASS
  // Duration is the authoritative field, end_time is derived
  const handleDurationChange = useCallback((newDuration) => {
    const duration = parseFloat(newDuration);
    if (isNaN(duration) || duration <= 0 || duration > 24) {
      // Still update the form state to allow typing
      setShiftForm(prev => ({ ...prev, duration: newDuration }));
      return;
    }
    
    // Calculate end_time from start_time + duration
    const newEndTime = shiftForm.start_time 
      ? calculateEndTime(shiftForm.start_time, duration)
      : "";
    
    setShiftForm(prev => ({ 
      ...prev, 
      duration: newDuration,
      end_time: newEndTime || prev.end_time
    }));
  }, [shiftForm.start_time, calculateEndTime]);

  // Handle start time change - recalculate end_time from duration
  const handleStartTimeChange = useCallback((newStartTime) => {
    const duration = parseFloat(shiftForm.duration);
    let newEndTime = shiftForm.end_time;
    
    if (!isNaN(duration) && duration > 0) {
      newEndTime = calculateEndTime(newStartTime, duration) || newEndTime;
    }
    
    setShiftForm(prev => ({
      ...prev,
      start_time: newStartTime,
      end_time: newEndTime
    }));
  }, [shiftForm.duration, shiftForm.end_time, calculateEndTime]);

  // Is overnight shift
  const isOvernightShift = useMemo(() => {
    if (!shiftForm.start_time || !shiftForm.end_time) return false;
    return shiftForm.end_time <= shiftForm.start_time;
  }, [shiftForm.start_time, shiftForm.end_time]);

  // Validate shift form
  const validateShiftForm = useCallback(() => {
    const errors = {};
    
    // Duration validation
    const duration = parseFloat(shiftForm.duration);
    if (!shiftForm.duration || isNaN(duration) || duration <= 0) {
      errors.duration = "Duration must be greater than 0";
    } else if (duration > 24) {
      errors.duration = "Duration cannot exceed 24 hours";
    }
    
    // Venue validation - venue_id is required
    if (!shiftForm.venue_id) {
      errors.venue = "Please select a venue";
    }
    
    // Venue Location validation - venue_location_id is required
    if (!shiftForm.venue_location_id) {
      errors.venue_location = "Please select a location";
    }
    
    // Date bounds validation (if project has dates)
    if (shiftForm.date && project) {
      const shiftDate = new Date(shiftForm.date);
      if (project.start_date && shiftDate < new Date(project.start_date)) {
        errors.date = `Shift date must be on or after project start (${formatDate(project.start_date)})`;
      }
      if (project.end_date && shiftDate > new Date(project.end_date)) {
        errors.date = `Shift date must be on or before project end (${formatDate(project.end_date)})`;
      }
    }
    
    return errors;
  }, [shiftForm.date, shiftForm.duration, shiftForm.venue_id, shiftForm.venue_location_id, project, formatDate]);

  // Shift form handlers
  const handleOpenShiftDialog = () => {
    setSelectedShift(null);
    setEditingShift(null);
    setShiftValidationErrors({});
    setRuleAddedSupplements([]); // Clear rule-added supplements for new shift
    
    // Get defaults from previous shift (if any)
    const sortedShifts = [...shifts].sort((a, b) => {
      // Sort by date first, then by start_time
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return (a.start_time || "").localeCompare(b.start_time || "");
    });
    const lastShift = sortedShifts[sortedShifts.length - 1];
    
    // Determine defaults based on whether this is first shift or subsequent
    const isFirstShift = shifts.length === 0;
    
    // Calculate default duration from previous shift
    const defaultDuration = lastShift?.duration_hours || "";
    
    // EVENTSFLO-SHIFT-INHERIT-PROJECT-VENUE-AND-QUOTE-FILENAME-FIX-V1
    // Determine defaults for Venue / Venue-Location:
    //   1. FIRST shift → inherit the Project's `default_venue_id` (was
    //      previously left blank, forcing the user to re-pick the venue
    //      they already chose at project creation).
    //   2. SUBSEQUENT shifts → keep the existing "copy from last shift"
    //      behaviour, and only fall back to the project default if the
    //      last shift has no venue (defensive).
    // Venue-location pre-fill: when the resolved venue has exactly ONE
    // location on record (the common case created together by the
    // QuickAddVenueDialog), auto-select it. If the venue has multiple
    // locations, leave the field blank so the user makes an explicit
    // choice (matches prior UX for multi-location venues). Never
    // overwrites a manual selection made after the form opens — this
    // block only sets the INITIAL values of the create form.
    const projectVenueId = project?.default_venue_id || "";
    const resolveSingleLocation = (venueId) => {
      if (!venueId) return "";
      const locs = (venueLocations || []).filter((vl) => vl.venue_id === venueId);
      return locs.length === 1 ? locs[0].id : "";
    };
    const defaultVenueId = isFirstShift
      ? projectVenueId
      : (lastShift?.venue_id || projectVenueId);
    const defaultVenueLocationId = isFirstShift
      ? (resolveSingleLocation(projectVenueId))
      : (lastShift?.venue_location_id || resolveSingleLocation(defaultVenueId));
    
    // Prepare form defaults
    const formDefaults = {
      venue_id: defaultVenueId,
      venue_location_id: defaultVenueLocationId,
      name: "",
      // First shift: project start date, subsequent: previous shift date
      date: isFirstShift 
        ? (project?.start_date || "") 
        : (lastShift?.date || project?.start_date || ""),
      // Subsequent shifts: copy previous times
      start_time: isFirstShift ? "" : (lastShift?.start_time || ""),
      duration: defaultDuration.toString(),
      end_time: isFirstShift ? "" : (lastShift?.end_time || ""),
      staff_required: isFirstShift ? 1 : (lastShift?.staff_required || 1),
      // EF-SHIFT-STAFFING-FIELD-PARAMETERIZATION: Default to "Crew"
      primary_role_id: DEFAULT_CREW_ID,
      status: "draft",
      notes: "",
      // EF-SHIFT-MEETING-PLACE-V1: Meeting place instructions
      meeting_place: "",
      // EVENTSFLO-SHIFT-ON-SITE-CONTACT-FROM-CLIENT-CONTACTS-V1
      on_site_contact_id: "",
      // EVENTSFLO-SHIFT-RULE-SUPPLEMENT-TASK-OVERRIDE-V1
      meal_supplement_task_definition_id: "",
      crew_chief_supplement_task_definition_id: "",
    };
    
    setShiftForm(formDefaults);
    setShiftTasks([]);
    
    // EF-DUPLICATION-PRECREATE-UX-ADJUSTMENT v1.1: 
    // Show duplication modal BEFORE Edit Shift on first shift of multi-day project
    if (isFirstShift && isMultiDayProject) {
      setPendingShiftOpen(true);
      setDuplicationModalOpen(true);
    } else {
      setShiftDialogOpen(true);
    }
  };

  // EVENTSFLO-CREW-PLANNER-CONFIRMED-STATUS-AND-SHIFT-NAVIGATION-FIX-V1
  // Fire the Edit Shift dialog once the shifts list is loaded AND a
  // pending `?editShift=<id>` param was captured on arrival. Runs
  // once per pending id, then clears the ref so re-renders don't
  // re-open the dialog.
  useEffect(() => {
    const pendingId = pendingEditShiftIdRef.current;
    if (!pendingId || shifts.length === 0) return;
    const target = shifts.find((s) => s.id === pendingId);
    if (target) {
      pendingEditShiftIdRef.current = null;
      // EVENTSFLO-CREW-PLANNER-SHIFT-EDIT-RETURN-AND-CONFIRMATION-AUDIT-V1
      // Latch the Crew-Planner-origin bit so the dialog-close effect
      // can route the user back to Crew Planner when the dialog is
      // dismissed (X / Cancel / Save / Discard).
      if (crewPlannerReturnUrlRef.current) {
        shiftDialogWasOpenFromCrewPlannerRef.current = true;
      }
      handleEditShift(target);
    }
  }, [shifts]);

  // EVENTSFLO-PROJECT-CREATE-NEXT-STEP-ADD-SHIFT-V1
  // Consume the `?addShift=true` deep-link once the project + venues
  // + task definitions are loaded. Opens the Add Shift dialog with
  // the project's default venue / venue-location inherited (existing
  // handleOpenShiftDialog behaviour for a first-shift case).
  // Guarded so:
  //   * fires only when the pending ref is true (create flow only);
  //   * fires only after `project` is loaded so
  //     `handleOpenShiftDialog` can inherit `default_venue_id`;
  //   * clears the ref before opening so a refresh cannot re-fire;
  //   * project *edit* never touches the ref → auto-open never fires.
  useEffect(() => {
    if (!pendingAddShiftRef.current) return;
    if (!project) return;
    if (loading) return;
    pendingAddShiftRef.current = false;
    handleOpenShiftDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, loading]);

  // EVENTSFLO-CREW-PLANNER-SHIFT-EDIT-RETURN-AND-CONFIRMATION-AUDIT-V1
  // When the Edit Shift dialog transitions from open → closed and
  // it was opened via the Crew Planner deep-link, send the user
  // back to their Crew Planner view (once — the ref is cleared).
  // Any regular Project-Detail-launched edit stays on the Project
  // screen because the ref is null.
  useEffect(() => {
    if (shiftDialogOpen) return;
    if (!shiftDialogWasOpenFromCrewPlannerRef.current) return;
    const returnUrl = crewPlannerReturnUrlRef.current;
    // Clear both latches before navigating to avoid re-triggering.
    shiftDialogWasOpenFromCrewPlannerRef.current = false;
    crewPlannerReturnUrlRef.current = null;
    if (returnUrl) {
      navigate(returnUrl);
    }
  }, [shiftDialogOpen, navigate]);



  // Open dialog for editing a shift
  const handleEditShift = async (shift) => {
    setEditingShift(shift);
    setEditingShiftRuleStatus(null);  // EVENTSFLO-SHIFT-RULE-SUPPRESSED-ITEM-LABEL-VISIBILITY-FIX-V1
    setShiftValidationErrors({});
    
    // Duration is loaded directly from DB - first-class field
    const duration = shift.duration_hours || "";
    
    // EF-SHIFT-STAFFING-FIELD-PARAMETERIZATION: Get current primary role from line items
    // EF-SHIFT-MANUAL-TASK-LINEITEM-FIX: Load ALL items from shift_task_line_items
    let primaryRoleId = DEFAULT_CREW_ID;
    let existingTasks = [];
    
    try {
      const lineItemsRes = await getShiftLineItems(shift.id);
      const lineItems = lineItemsRes.data?.items || [];
      
      // Find primary role
      const primaryItem = lineItems.find(item => item.type_snapshot === 'primary');
      if (primaryItem?.task_definition_id) {
        primaryRoleId = primaryItem.task_definition_id;
      }
      
      // Map line items to shiftTasks format (excludes primary staffing item)
      existingTasks = lineItems
        .filter(item => item.type_snapshot !== 'primary') // Primary is handled separately
        .map(item => {
          const taskDef = getTaskDefinitionById(item.task_definition_id);
          const taskName = item.name_snapshot || taskDef?.name || "Unknown";
          
          // Detect travel tasks by name pattern
          const isTravelByName = 
            taskName.toLowerCase().includes('travel crew time') ||
            taskName.toLowerCase().includes('travel distance') ||
            taskName.toLowerCase().includes('travel toll') ||
            taskName.toLowerCase().includes('road toll');
          
          // EF-EDIT-SHIFT-HOURLY-BINDING-CORRECTION: Include duration_hours from line item
          return {
            id: item.id,
            task_definition_id: item.task_definition_id,
            task_name: taskName,
            quantity: item.quantity || 1,
            duration_hours: item.duration_hours || 0, // Load stored duration
            charge_rate: item.unit_rate || 0,
            pay_rate: taskDef?.default_pay_rate || 0,
            charge_unit: taskDef?.default_charge_unit || "hourly",
            supplement_group: item.supplement_group_snapshot || (isTravelByName ? "travel" : null),
            is_travel_task: isTravelByName,
            rule_added: item.rule_added || false,
            persisted: true,
          };
        });
    } catch (err) {
      console.error("Failed to load shift line items:", err);
      existingTasks = [];
    }
    
    setShiftForm({
      venue_id: shift.venue_id || "",
      venue_location_id: shift.venue_location_id || "",
      name: shift.name || "",
      date: shift.date || "",
      start_time: shift.start_time || "",
      duration: duration.toString(),
      end_time: shift.end_time || "",
      staff_required: shift.staff_required || 1,
      // EF-SHIFT-STAFFING-FIELD-PARAMETERIZATION: Load current primary role
      primary_role_id: primaryRoleId,
      status: shift.status || "draft",
      notes: shift.notes || "",
      // EF-SHIFT-MEETING-PLACE-V1: Load meeting place
      meeting_place: shift.meeting_place || "",
      out_of_town: shift.out_of_town || false,
      // EVENTSFLO-SHIFT-ON-SITE-CONTACT-FROM-CLIENT-CONTACTS-V1
      on_site_contact_id: shift.on_site_contact_id || "",
      // EVENTSFLO-SHIFT-RULE-SUPPLEMENT-TASK-OVERRIDE-V1
      meal_supplement_task_definition_id: shift.meal_supplement_task_definition_id || "",
      crew_chief_supplement_task_definition_id: shift.crew_chief_supplement_task_definition_id || "",
    });
    
    setShiftTasks(existingTasks);
    
    // Populate rule-added supplements for visibility message
    const ruleAdded = existingTasks.filter(t => t.rule_added);
    setRuleAddedSupplements(ruleAdded.map(t => t.task_name));
    
    setShiftDialogOpen(true);
  };

  // Duplicate a shift and open in edit mode
  const handleDuplicateShift = async (shift) => {
    try {
      const res = await duplicateShift(shift.id);
      const { shift: newShift, copied_tasks_count, copied_crew_count, copied_line_items_count } = res.data;
      
      // EF-SHIFT-DUPLICATION-DIAGNOSTIC-V1: Improved toast with accurate data
      toast.success(
        `Shift duplicated: ${copied_tasks_count || copied_line_items_count || 0} tasks, ${copied_crew_count} crew`
      );
      
      // EF-SHIFT-DUPLICATION-DIAGNOSTIC-V1: HARD REFRESH from API (not stale state)
      const shiftsRes = await getShifts({ project_id: id });
      const updatedShifts = shiftsRes.data || [];
      setShifts(updatedShifts);
      
      // Log for debugging
      console.log(`[SHIFT LIST] Total shifts from API: ${updatedShifts.length}`);
      
      // Open the new shift in edit mode
      const duplicatedShift = updatedShifts.find(s => s.id === newShift.id);
      if (duplicatedShift) {
        handleEditShift(duplicatedShift);
      } else {
        console.warn(`[SHIFT LIST] New shift ${newShift.id} not found in refreshed list`);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  // Handle form field changes with validation
  const handleShiftFieldChange = (field, value) => {
    let newForm = { ...shiftForm, [field]: value };
    
    // SMART DEFAULT: When Start Time changes, auto-set End Time if empty or invalid
    if (field === 'start_time' && value) {
      const startMinutes = timeToMinutes(value);
      const endMinutes = timeToMinutes(newForm.end_time);
      
      // If End Time is empty OR earlier than/equal to Start Time, default to Start + 1 hour
      if (!newForm.end_time || endMinutes <= startMinutes) {
        const defaultEndMinutes = startMinutes + 60; // +1 hour
        newForm.end_time = minutesToTime(defaultEndMinutes >= 1440 ? defaultEndMinutes - 1440 : defaultEndMinutes);
      }
    }
    
    setShiftForm(newForm);
    
    // EF-STABILIZATION-REGRESSION-ROUND-2: When crew count changes, update Travel CREW Time quantity
    if (field === 'staff_required' && newForm.out_of_town) {
      const newCrewCount = parseInt(value) || 1;
      setShiftTasks(prev => prev.map(task => {
        if (task.task_name?.toLowerCase().includes('travel crew time') || 
            task.task_name === 'Travel CREW Time') {
          return { ...task, quantity: newCrewCount };
        }
        return task;
      }));
    }
    
    // Run live validation for date and time fields
    if (field === 'date' || field === 'start_time' || field === 'end_time') {
      const newErrors = { ...shiftValidationErrors };
      
      // Validate date bounds
      if (newForm.date && project) {
        const shiftDate = new Date(newForm.date);
        if (project.start_date && shiftDate < new Date(project.start_date)) {
          newErrors.date = `Shift date must be on or after project start (${formatDate(project.start_date)})`;
        } else if (project.end_date && shiftDate > new Date(project.end_date)) {
          newErrors.date = `Shift date must be on or before project end (${formatDate(project.end_date)})`;
        } else {
          delete newErrors.date;
        }
      } else {
        delete newErrors.date;
      }
      
      // Validate time (start equals end)
      if (newForm.start_time && newForm.end_time && newForm.start_time === newForm.end_time) {
        newErrors.time = "Start and end time cannot be the same";
      } else {
        delete newErrors.time;
      }
      
      setShiftValidationErrors(newErrors);
    }
  };
  
  // Helper: Convert HH:MM to minutes since midnight
  const timeToMinutes = (time) => {
    if (!time) return null;
    const [h, m] = time.split(':').map(Number);
    return h * 60 + m;
  };
  
  // Helper: Convert minutes since midnight to HH:MM
  const minutesToTime = (minutes) => {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // PART A: OOT toggle handler - AUTHORITATIVE BEHAVIOR
  // - OOT ON: Insert the 3 travel supplements ONCE (idempotent across toggle cycles)
  // - OOT OFF: Show warning, do NOT auto-delete
  // EVENTSFLO-OOT-RATE-DRIFT-HANDLE-OOT-TOGGLE-FIX-V1
  // Recurring bug: toggling OOT could re-inject defaults over already-edited
  // line items because the existence guard only used heuristic name/flag
  // matches. If a persisted item had a non-standard name, a null
  // `supplement_group_snapshot`, or was a rule-added travel row (e.g. OOT
  // car), the guard missed it, defaults were appended, and on save the
  // user's edited rates were drowned by default-priced duplicates.
  // Fix: load the 3 travel TaskDefinitions FIRST, then check existence by
  // the authoritative `task_definition_id` (with heuristics as fallback)
  // before injecting any defaults. Existing items — and their persisted
  // user-edited rates/quantities — are never touched.
  const handleOOTToggle = useCallback(async (checked) => {
    setShiftForm(prev => ({ ...prev, out_of_town: checked }));

    if (!checked) {
      // OOT turned OFF - show warning, do NOT auto-delete
      const travelTasksPresent = shiftTasks.some(t =>
        t.supplement_group === "travel" || t.is_travel_task
      );
      if (travelTasksPresent && !ootOffWarningShown) {
        toast.warning(
          "OOT has been disabled. Travel supplements remain. Please remove them manually if not required.",
          { duration: 5000 }
        );
        setOotOffWarningShown(true);
      }
      return;
    }

    // OOT turned ON - hydrate-then-inject
    try {
      const response = await getTaskDefinitions({ limit: 500, enabled_filter: "enabled" });
      const allTasks = response.data?.items || response.data || [];

      // Resolve the 3 standard travel TaskDefinitions (active only).
      const travelCrewTime = allTasks.find(t =>
        t.is_active && (t.name === "Travel CREW Time" || t.name?.toLowerCase().includes("travel crew time"))
      );
      const travelDistance = allTasks.find(t =>
        t.is_active && (t.name === "Travel Distance (KM)" || t.name?.toLowerCase().includes("travel distance"))
      );
      const travelToll = allTasks.find(t =>
        t.is_active && (t.name === "Travel Toll" || t.name?.toLowerCase().includes("travel toll"))
      );

      // Authoritative existence check: match by task_definition_id against
      // the 3 known travel TaskDefs, falling back to heuristic flags so we
      // still catch legacy/rule-added travel rows whose def id is absent.
      const travelDefIds = new Set(
        [travelCrewTime?.id, travelDistance?.id, travelToll?.id].filter(Boolean)
      );
      const existingTravelTasks = shiftTasks.filter(t =>
        (t.task_definition_id && travelDefIds.has(t.task_definition_id)) ||
        t.supplement_group === "travel" ||
        t.is_travel_task
      );

      if (existingTravelTasks.length > 0) {
        // Existing travel items found — preserve their user-edited rates
        // and quantities. Toggling ON over an existing set is a no-op.
        toast.info("Travel supplements already present. Existing rates preserved.");
        setOotOffWarningShown(false);
        return;
      }

      const newTravelTasks = [];

      if (travelCrewTime) {
        // EF-STABILIZATION-REGRESSION-ROUND-2: Use ref to get CURRENT crew count (avoids stale closure)
        const currentCrewCount = parseInt(shiftFormRef.current.staff_required) || 1;
        newTravelTasks.push({
          task_definition_id: travelCrewTime.id,
          task_name: travelCrewTime.name,
          quantity: currentCrewCount,
          charge_rate: travelCrewTime.default_charge_rate || 0,
          pay_rate: travelCrewTime.default_pay_rate || 0,
          charge_unit: travelCrewTime.charge_unit || "hourly",
          supplement_group: "travel",
          is_travel_task: true,
          persisted: false,
        });
      }

      if (travelDistance) {
        newTravelTasks.push({
          task_definition_id: travelDistance.id,
          task_name: travelDistance.name,
          quantity: 1,
          charge_rate: travelDistance.default_charge_rate || 0,
          pay_rate: travelDistance.default_pay_rate || 0,
          charge_unit: travelDistance.charge_unit || "km",
          supplement_group: "travel",
          is_travel_task: true,
          persisted: false,
        });
      }

      if (travelToll) {
        newTravelTasks.push({
          task_definition_id: travelToll.id,
          task_name: travelToll.name,
          quantity: 1,
          charge_rate: travelToll.default_charge_rate || 0,
          pay_rate: travelToll.default_pay_rate || 0,
          charge_unit: travelToll.charge_unit || "fixed",
          supplement_group: "travel",
          is_travel_task: true,
          persisted: false,
        });
      }

      if (newTravelTasks.length > 0) {
        setShiftTasks(prev => [...prev, ...newTravelTasks]);
        toast.success(`Added ${newTravelTasks.length} travel supplement(s).`);
      }
      setOotOffWarningShown(false);
    } catch (err) {
      console.error("Failed to load travel tasks:", err);
      toast.error("Failed to load travel supplements");
    }
  }, [shiftTasks, ootOffWarningShown]);

  // EF-TRAVEL-ADVISOR-01: Handle Travel Advisor modal open
  const handleOpenTravelAdvisor = useCallback(async () => {
    // Load crew list for address selection - only active staff with addresses
    try {
      const staffRes = await getStaffList({ status: 'active', limit: 500 });
      setCrewList(staffRes.data?.items || staffRes.data || []);
    } catch (err) {
      console.error("Failed to load crew list:", err);
      setCrewList([]);
    }
    setTravelAdvisorOpen(true);
  }, []);

  // EF-TRAVEL-ADVISOR-01: Apply travel advisory values to travel tasks
  const handleApplyTravelAdvisory = useCallback((values) => {
    // Find or create travel tasks with advisory values
    const travelTasks = shiftTasks.filter(t => t.supplement_group === "travel");
    
    // EF-OOT-SUPPLEMENT-REGRESSION: Get current crew count for Travel CREW Time
    const currentCrewCount = parseInt(shiftFormRef.current.staff_required) || 1;
    
    // Update existing travel tasks or create new ones
    setShiftTasks(prev => {
      const updated = [...prev];
      
      // Find travel distance task
      const distanceTask = updated.find(t => 
        t.task_name?.toLowerCase().includes("distance") || 
        t.task_name?.toLowerCase().includes("km")
      );
      if (distanceTask) {
        distanceTask.quantity = values.distance_km;
      }
      
      // EF-OOT-SUPPLEMENT-REGRESSION: Travel CREW Time uses quantity=crew, duration_hours=travel time
      const timeTask = updated.find(t => 
        t.task_name?.toLowerCase().includes("crew time")
      );
      if (timeTask) {
        // quantity = crew count, duration_hours = travel time
        timeTask.quantity = currentCrewCount;
        timeTask.duration_hours = Math.round(values.duration_hours * 4) / 4; // Round to quarter hour
      }
      
      // Find toll task
      const tollTask = updated.find(t => 
        t.task_name?.toLowerCase().includes("toll")
      );
      if (tollTask) {
        tollTask.quantity = values.toll_estimate;
      }
      
      return updated;
    });
    
    toast.success("Travel advisory values applied. Save the shift to persist changes.");
  }, [shiftTasks]);

  // Get venue address for Travel Advisor
  const getVenueAddressForAdvisor = useCallback(() => {
    if (!shiftForm.venue_id) return "";
    
    const venue = venues.find(v => v.id === shiftForm.venue_id);
    if (!venue) return "";
    
    const location = venueLocations.find(l => l.id === shiftForm.venue_location_id);
    
    // Build address from venue and location
    const parts = [];
    if (location?.address) parts.push(location.address);
    else if (venue?.address) parts.push(venue.address);
    if (venue?.city) parts.push(venue.city);
    if (venue?.country) parts.push(venue.country);
    
    return parts.join(", ");
  }, [shiftForm.venue_id, shiftForm.venue_location_id, venues, venueLocations]);

  // Add a task to the shift
  // PLATFORM LAW: Tasks must persist immediately when editing existing shift
  // EF-SHIFT-MANUAL-TASK-LINEITEM-FIX: Use shift_task_line_items, not legacy shift_tasks
  const handleAddTask = async (taskDefId) => {
    const taskDef = getTaskDefinitionById(taskDefId);
    if (!taskDef) return;
    
    // Check if already added (check both local state and line items)
    if (shiftTasks.find((t) => t.task_definition_id === taskDefId)) {
      toast.error("Task already added to this shift");
      return;
    }

    // EF-TASKITEM-DEFAULT-DURATION-UX: Pre-fill duration with shift duration
    const shiftDuration = parseFloat(shiftForm.duration) || 0;

    const newTask = {
      task_definition_id: taskDefId,
      task_name: taskDef.name,
      quantity: 1,
      charge_rate: taskDef.default_charge_rate || 0,
      pay_rate: taskDef.default_pay_rate || 0,
      charge_unit: taskDef.default_charge_unit || "hourly",
      supplement_group: taskDef.supplement_group || null,
      duration_hours: shiftDuration, // Default to shift duration
      persisted: false,
    };

    // If editing existing shift, persist immediately as ShiftTaskLineItem
    if (editingShift) {
      const payload = {
        shift_id: editingShift.id,
        task_definition_id: taskDefId,
        quantity: 1,
        duration_hours: shiftDuration, // Include default duration
      };
      
      try {
        const response = await createShiftLineItem(payload);
        
        // Update with persisted data
        newTask.id = response.data.id;
        newTask.persisted = true;
        newTask.charge_rate = response.data.unit_rate || taskDef.default_charge_rate || 0;
        newTask.duration_hours = response.data.duration_hours || shiftDuration;
        
        toast.success(`${taskDef.name} added to shift`);
      } catch (err) {
        console.error("Failed to add task:", err);
        toast.error(`Failed to add task: ${extractErrorMessage(err)}`);
        return;
      }
    }

    setShiftTasks([...shiftTasks, newTask]);
  };

  // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
  // Add a row to the new "Associated Costs / Supplements" section.
  //
  // - Defaults quantity to 1 (NEVER to staff_required) — supplement
  //   quantity is independent of Crew Required.
  // - Tags the local row with type_snapshot='supplement' so the create
  //   flow's `createShiftTasksForShift` can pass it through to the
  //   backend and avoid the orphan-category misclassification fallback.
  // - For editing-existing-shift the row is immediately persisted with
  //   explicit type_snapshot='supplement', then mirrored into the
  //   local state.
  const handleAddSupplementTask = async (taskDefId) => {
    const taskDef = getTaskDefinitionById(taskDefId);
    if (!taskDef) return;

    if (shiftTasks.find((t) => t.task_definition_id === taskDefId)) {
      toast.error("Supplement already added to this shift");
      return;
    }

    const shiftDuration = parseFloat(shiftForm.duration) || 0;
    const newTask = {
      task_definition_id: taskDefId,
      task_name: taskDef.name,
      quantity: 1,
      charge_rate: taskDef.default_charge_rate || 0,
      pay_rate: taskDef.default_pay_rate || 0,
      charge_unit: taskDef.default_charge_unit || "hourly",
      supplement_group: taskDef.supplement_group || null,
      duration_hours: shiftDuration,
      persisted: false,
      type_snapshot: "supplement",
    };

    if (editingShift) {
      try {
        const response = await createShiftLineItem({
          shift_id: editingShift.id,
          task_definition_id: taskDefId,
          quantity: 1,
          duration_hours: shiftDuration,
          type_snapshot: "supplement",
        });
        newTask.id = response.data.id;
        newTask.persisted = true;
        newTask.charge_rate = response.data.unit_rate || taskDef.default_charge_rate || 0;
        newTask.duration_hours = response.data.duration_hours || shiftDuration;
        toast.success(`${taskDef.name} added as supplement`);
      } catch (err) {
        console.error("Failed to add supplement:", err);
        toast.error(`Failed to add supplement: ${extractErrorMessage(err)}`);
        return;
      }
    }

    setShiftTasks([...shiftTasks, newTask]);
  };

  // Update task quantity
  // PLATFORM LAW: Changes persist immediately when editing existing shift
  // STABILIZE_OOT_TRAVEL_ONLY_V1: Fixed numeric input handling
  // - Allows 0 values for travel supplements
  // - Handles empty string for backspace/clearing
  // - Properly converts string to number on blur/submit
  const handleUpdateTaskQuantity = async (taskDefId, value) => {
    const task = shiftTasks.find(t => t.task_definition_id === taskDefId);
    
    // Allow empty string while typing (for backspace behavior)
    const numValue = value === "" ? "" : parseFloat(value);
    const displayValue = value === "" ? "" : (isNaN(numValue) ? 0 : numValue);
    
    // For travel supplements, allow 0 values
    const isTravelTask = task?.supplement_group === "travel" || task?.is_travel_task;
    const finalValue = isTravelTask ? displayValue : (displayValue === "" ? 0 : Math.max(0, displayValue));
    
    // EF-LINEITEM-EDIT-PERSISTENCE-AUDIT: Use updateShiftLineItem with complete snapshot
    if (editingShift && task?.persisted && task?.id && finalValue !== "") {
      try {
        await updateShiftLineItem(task.id, { 
          quantity: finalValue || 0,
          unit_rate: task.charge_rate || 0,
          duration_hours: task.duration_hours || 0,
        });
      } catch (err) {
        console.error("Failed to update task quantity:", err);
      }
    }
    
    setShiftTasks(
      shiftTasks.map((t) =>
        t.task_definition_id === taskDefId
          ? { ...t, quantity: finalValue }
          : t
      )
    );
  };
  
  // STABILIZE_OOT_TRAVEL_ONLY_V1: Handle rate updates for travel supplements
  // EF-OOT-SUPPLEMENT-PERSISTENCE-FIX-V1: Enhanced with debug logging and explicit number handling
  const handleUpdateTaskRate = async (taskDefId, value) => {
    const task = shiftTasks.find(t => t.task_definition_id === taskDefId);
    
    // Allow empty string while typing
    const numValue = value === "" ? "" : parseFloat(value);
    const displayValue = value === "" ? "" : (isNaN(numValue) ? 0 : numValue);
    
    // EF-OOT-SUPPLEMENT-PERSISTENCE-FIX-V1: Debug logging for rate persistence
    console.log("[OOT Rate Update]", {
      taskDefId,
      inputValue: value,
      displayValue,
      taskId: task?.id,
      persisted: task?.persisted,
      editingShift: !!editingShift,
      willPersist: !!(editingShift && task?.persisted && task?.id && displayValue !== "")
    });
    
    // EF-LINEITEM-EDIT-PERSISTENCE-AUDIT: Use updateShiftLineItem with complete snapshot
    // EF-OOT-SUPPLEMENT-PERSISTENCE-FIX-V1: Ensure numeric rate (0 is valid, not falsy for our purposes)
    if (editingShift && task?.persisted && task?.id && displayValue !== "") {
      try {
        const updatePayload = { 
          quantity: task.quantity || 1,
          unit_rate: typeof displayValue === 'number' ? displayValue : parseFloat(displayValue) || 0,
          duration_hours: task.duration_hours || 0,
        };
        console.log("[OOT Rate Update] Persisting:", { lineItemId: task.id, ...updatePayload });
        await updateShiftLineItem(task.id, updatePayload);
        console.log("[OOT Rate Update] Persistence successful");
      } catch (err) {
        console.error("[OOT Rate Update] Failed to persist:", err);
      }
    }
    
    setShiftTasks(
      shiftTasks.map((t) =>
        t.task_definition_id === taskDefId
          ? { ...t, charge_rate: displayValue }
          : t
      )
    );
  };

  // EF-HOURLY-TASK-UI-BINDING-FIX: Handle duration updates for hourly items
  const handleUpdateTaskDuration = async (taskDefId, value) => {
    const task = shiftTasks.find(t => t.task_definition_id === taskDefId);
    
    // Allow empty string while typing
    const numValue = value === "" ? "" : parseFloat(value);
    const displayValue = value === "" ? "" : (isNaN(numValue) ? 0 : Math.max(0, numValue));
    
    // EF-LINEITEM-EDIT-PERSISTENCE-AUDIT: Use updateShiftLineItem with complete snapshot
    if (editingShift && task?.persisted && task?.id && displayValue !== "") {
      try {
        await updateShiftLineItem(task.id, { 
          quantity: task.quantity || 1,
          unit_rate: task.charge_rate || 0,
          duration_hours: displayValue || 0,
        });
      } catch (err) {
        console.error("Failed to update task duration:", err);
      }
    }
    
    setShiftTasks(
      shiftTasks.map((t) =>
        t.task_definition_id === taskDefId
          ? { ...t, duration_hours: displayValue }
          : t
      )
    );
  };

  // Remove a task from the shift
  // PLATFORM LAW: Deletions persist immediately when editing existing shift
  const handleRemoveTask = async (taskDefId) => {
    const task = shiftTasks.find(t => t.task_definition_id === taskDefId);
    
    // If editing and task is persisted, delete from backend
    if (editingShift && task?.persisted && task?.id) {
      try {
        await deleteShiftTask(task.id);
        toast.success("Task removed from shift");
      } catch (err) {
        toast.error(`Failed to remove task: ${extractErrorMessage(err)}`);
        return; // Don't remove from local state if deletion failed
      }
    }
    
    setShiftTasks(shiftTasks.filter((t) => t.task_definition_id !== taskDefId));
  };

  // Create shift_tasks after shift creation
  // EF-SHIFT-NEW-CREATION-TASK-STAGING: Create line items for tasks added during new shift creation
  // Only processes unpersisted tasks (those added before shift_id existed)
  // EF-OOT-SUPPLEMENT-REGRESSION: Accept createdTaskDefIds to avoid duplicates
  const createShiftTasksForShift = async (shiftId, createdTaskDefIds = []) => {
    // Filter out persisted tasks AND tasks already created by rule resolutions
    const unpersistedTasks = shiftTasks.filter(t => 
      !t.persisted && !createdTaskDefIds.includes(t.task_definition_id)
    );
    if (unpersistedTasks.length === 0) return [];
    
    // EF-OOT-SUPPLEMENT-REGRESSION: Get current crew count for Travel CREW Time binding
    const currentCrewCount = parseInt(shiftFormRef.current.staff_required) || 1;
    
    const results = [];
    for (const task of unpersistedTasks) {
      try {
        // EF-OOT-SUPPLEMENT-REGRESSION: Travel CREW Time must use same quantity as primary staffing
        const isTraveCrewTime = task.task_name?.toLowerCase().includes('travel crew time') || 
                                task.task_name === 'Travel CREW Time';
        const taskQuantity = isTraveCrewTime ? currentCrewCount : task.quantity;

        // EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
        // Forward an explicit type_snapshot='supplement' for rows added
        // via the Create-Shift Associated Costs section so the backend
        // never has to fall back to category-key inference.
        // EVENTSFLO-SHIFT-SUPPLEMENT-HOURLY-CALC-EDIT-STABILITY-V2
        // For supplements with an HOURLY charge unit, prefer the
        // CURRENT shift duration over the (possibly stale) value
        // captured at add-time. Fixed/unit supplements ignore hours
        // entirely so we keep their captured duration as-is.
        const isSupp = (task.type_snapshot || "").toLowerCase() === "supplement";
        const chargeUnit = (task.charge_unit || "").toLowerCase();
        const isHourlyCharge = chargeUnit === "hourly"
          || chargeUnit === "hour" || chargeUnit === "per_hour";
        const liveShiftHours = parseFloat(shiftForm?.duration) || 0;
        const effectiveDuration = (isSupp && isHourlyCharge && liveShiftHours > 0)
          ? liveShiftHours
          : task.duration_hours;
        const payload = {
          shift_id: shiftId,
          task_definition_id: task.task_definition_id,
          quantity: taskQuantity,
          duration_hours: effectiveDuration, // EF-TASKITEM-DEFAULT-DURATION-UX
          unit_rate: task.charge_rate, // EF-OOT-TRAVEL-SUPPLEMENT-BUG-V1
        };
        if (isSupp) {
          payload.type_snapshot = "supplement";
        }
        const response = await createShiftLineItem(payload);
        results.push(response.data);
      } catch (err) {
        console.error(`Failed to create line item: ${task.task_name}`, err);
        // Continue with other tasks even if one fails
      }
    }
    return results;
  };

  const handleShiftSubmit = async (e, keepOpen = false) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateShiftForm();
    if (Object.keys(errors).length > 0) {
      setShiftValidationErrors(errors);
      toast.error(Object.values(errors)[0]);
      return;
    }
    
    setShiftSubmitting(true);
    
    try {
      // Parse duration as float
      const durationValue = parseFloat(shiftForm.duration);
      
      // Calculate end_time from start_time + duration
      const derivedEndTime = calculateEndTime(shiftForm.start_time, durationValue);
      
      const data = {
        project_id: id,
        // New venue structure - both required
        venue_id: shiftForm.venue_id,
        venue_location_id: shiftForm.venue_location_id,
        name: shiftForm.name,
        date: shiftForm.date,
        start_time: shiftForm.start_time,
        // DURATION IS FIRST-CLASS FIELD - authoritative source
        duration_hours: durationValue,
        // end_time is derived from start_time + duration
        end_time: derivedEndTime || shiftForm.end_time,
        staff_required: parseInt(shiftForm.staff_required, 10),
        // EF-SHIFT-STAFFING-FIELD-PARAMETERIZATION: Include selected primary role
        primary_task_definition_id: shiftForm.primary_role_id || null,
        status: shiftForm.status,
        notes: shiftForm.notes,
        // EF-SHIFT-MEETING-PLACE-V1: Meeting place instructions
        meeting_place: shiftForm.meeting_place?.trim() || null,
        out_of_town: shiftForm.out_of_town || false,
        // EVENTSFLO-SHIFT-ON-SITE-CONTACT-FROM-CLIENT-CONTACTS-V1
        // Send null on explicit clear; backend snapshots the contact server-side.
        on_site_contact_id: shiftForm.on_site_contact_id || null,
        // EVENTSFLO-SHIFT-RULE-SUPPLEMENT-TASK-OVERRIDE-V1
        meal_supplement_task_definition_id: shiftForm.meal_supplement_task_definition_id || null,
        crew_chief_supplement_task_definition_id: shiftForm.crew_chief_supplement_task_definition_id || null,
      };

      if (editingShift) {
        // Update existing shift with rule evaluation
        // PLATFORM LAW: Rules must re-evaluate on EVERY shift save (create OR update)
        const response = await updateShiftWithRules(editingShift.id, data);
        const result = response.data;

        // ALWAYS refresh data after update to ensure UI shows persisted values
        await loadData();
        // EVENTSFLO-SHIFT-RULE-STATUS-SHIELD-NON-BLOCKING-V1
        // Re-fetch every visible badge after a rule-aware save.
        setShiftBadgeRefreshKey((k) => k + 1);

        // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
        // Save accepted — accept the current persisted state as the new
        // baseline so any subsequent X/Cancel cannot inverse-undo it.
        try { shiftLineItemsRef.current?.commit?.(); } catch { /* no-op */ }

        // Handle rule prompts if returned
        if (result.rule_prompts && result.rule_prompts.length > 0) {
          // Rules triggered - show resolution modal
          setRulePrompts(result.rule_prompts);
          setResolutionModalOpen(true);
          setShiftDialogOpen(false);
          setRuleAddedSupplements([]);
        } else {
          toast.success("Shift updated successfully");
          setShiftDialogOpen(false);
          setEditingShift(null);
          setRuleAddedSupplements([]);
        }
      } else {
        // Create new shift - preview rules first, then create if confirmed
        // EF-RULE-PREVIEW-SPLIT: Rules are previewed BEFORE persistence
        await previewAndMaybeCreate(data, keepOpen);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setShiftSubmitting(false);
    }
  };

  // EF-RULE-PREVIEW-SPLIT: Store pending shift data for after rule confirmation
  const [pendingShiftData, setPendingShiftData] = useState(null);
  const [pendingKeepOpen, setPendingKeepOpen] = useState(false);
  const [pendingRuleResolutions, setPendingRuleResolutions] = useState(null);

  // Helper to proceed with shift creation AFTER rule confirmation
  const proceedWithShiftCreation = async (data, keepOpen, ruleResolutions = null) => {
    const response = await createShiftWithRules(data);
    const result = response.data;
    const createdShift = result.shift;

    // EF-OOT-SUPPLEMENT-REGRESSION: Track created task def IDs to avoid duplicates
    const createdTaskDefIds = [];

    // EF-RULE-TASKDEFINITION-BINDING: Create rule supplements with EXACT TaskDefinition IDs
    if (ruleResolutions && createdShift) {
      for (const [ruleId, resolution] of Object.entries(ruleResolutions)) {
        if (resolution.taskDefId) {
          // Single select rule
          try {
            await createShiftLineItem({
              shift_id: createdShift.id,
              task_definition_id: resolution.taskDefId, // EXACT ID from user selection
              quantity: resolution.quantities?.[resolution.taskDefId] || resolution.quantity || 1,
              rule_added: true,
              rule_id: ruleId,
            });
            createdTaskDefIds.push(resolution.taskDefId);
          } catch (err) {
            console.error(`Failed to create rule supplement: ${ruleId}`, err);
          }
        } else if (resolution.taskDefIds?.length > 0) {
          // Multi-select rule
          for (const taskDefId of resolution.taskDefIds) {
            try {
              await createShiftLineItem({
                shift_id: createdShift.id,
                task_definition_id: taskDefId, // EXACT ID from user selection
                quantity: resolution.quantities?.[taskDefId] || 1,
                rule_added: true,
                rule_id: ruleId,
              });
              createdTaskDefIds.push(taskDefId);
            } catch (err) {
              console.error(`Failed to create rule supplement: ${ruleId}`, err);
            }
          }
        }
      }
    }

    // Create shift tasks if any were selected (skip already-created rule supplements)
    if (shiftTasks.length > 0 && createdShift) {
      await createShiftTasksForShift(createdShift.id, createdTaskDefIds);
    }

    // Handle duplication if planned
    if (duplicationPlan && duplicationPlan.dates.length > 0 && createdShift) {
      try {
        setDuplicationLoading(true);
        // EF-MULTIDAY-FIX: Pass dates array directly, not wrapped in object
        await duplicateShiftMultiDay(createdShift.id, duplicationPlan.dates);
        toast.success(`Shift duplicated to ${duplicationPlan.dates.length} additional dates`);
      } catch (err) {
        toast.error(`Failed to duplicate: ${extractErrorMessage(err)}`);
      } finally {
        setDuplicationLoading(false);
        setDuplicationPlan(null);
      }
    }

    // Clear pending data
    setPendingShiftData(null);
    setPendingKeepOpen(false);
    setPendingRuleResolutions(null);

    toast.success("Shift created successfully");
    setShiftDialogOpen(false);
    // Reset form to initial state
    setShiftForm({
      venue_id: "",
      venue_location_id: "",
      name: "",
      date: "",
      start_time: "",
      duration: "",
      end_time: "",
      staff_required: 1,
      primary_role_id: DEFAULT_CREW_ID,
      status: "draft",
      notes: "",
    });
    setShiftTasks([]);
    setEditingShift(null);
    await loadData();
    
    // Keep dialog open if requested (for quick multi-shift creation)
    if (keepOpen) {
      handleOpenShiftDialog();
    }
  };

  // EF-RULE-PREVIEW-SPLIT: Preview rules before creation
  const previewAndMaybeCreate = async (data, keepOpen) => {
    // Use the duration_hours from payload directly
    const durationHours = data.duration_hours || 0;

    // Get task definitions filtered by rule configs
    const mealTasks = getTaskDefinitionsForRule("MEAL");
    const crewChiefTasks = getTaskDefinitionsForRule("CREW_CHIEF");
    
    // Get rule configs for effect types
    const mealConfig = getRuleConfig("MEAL");
    const crewChiefConfig = getRuleConfig("CREW_CHIEF");
    
    // RULE EVALUATION (preview only - no shift created yet)
    const rulesTriggered = [];
    
    // RULE: Meal Allowance Required (shift > 5 hours)
    if (durationHours > 5 && mealTasks.length > 0) {
      rulesTriggered.push({
        rule_id: "meal_allowance_required",
        rule_name: "Meal Allowance Required",
        message: `This shift is ${durationHours.toFixed(1)} hours. Select the appropriate meal allowance for crew welfare.`,
        prompt_text: "This shift exceeds the meal break threshold. Select a meal allowance.",
        effect_type: "choice",
        task_definition_id: null,
        allowed_task_definition_ids: mealTasks.map(t => t.id),
        quantity_mode: "crew_count",
        quantity: 1,
        resolved_quantity: data.staff_required || 1,
        supplement_group: "meal",
        allow_multiple: mealConfig.allow_multiple || false,
      });
    }
    
    // RULE: Crew Chief Requirement (crew >= 4)
    if (data.staff_required >= 4 && crewChiefTasks.length > 0) {
      rulesTriggered.push({
        rule_id: "crew_chief_required",
        rule_name: "Crew Chief Requirement",
        message: `This shift has ${data.staff_required} crew members. Select the appropriate Crew Chief role.`,
        prompt_text: "This shift has 4 or more crew members. A Crew Chief supplement is required.",
        effect_type: "choice",
        task_definition_id: null,
        allowed_task_definition_ids: crewChiefTasks.map(t => t.id),
        quantity_mode: "fixed",
        quantity: 1,
        resolved_quantity: 1,
        supplement_group: "crew_chief",
        allow_multiple: crewChiefConfig.allow_multiple || false,
      });
    }

    // If rules triggered, show resolution modal BEFORE creating shift
    if (rulesTriggered.length > 0) {
      // Store pending data - shift will be created after user confirms
      setPendingShiftData(data);
      setPendingKeepOpen(keepOpen);
      setResolutionShiftId(null); // No shift ID yet
      setResolutionShiftData({
        duration_hours: durationHours,
        staff_required: data.staff_required,
        shift_name: data.name,
      });
      setFiredRules(rulesTriggered);
      setShiftDialogOpen(false);
      setResolutionModalOpen(true);
      return; // Don't create shift yet
    }

    // No rules triggered - create shift directly
    await proceedWithShiftCreation(data, keepOpen);
  };

  // Handle rule resolution modal completion
  // EF-RULE-PREVIEW-SPLIT: Handle rule resolution modal success
  // Now creates shift AFTER user confirms rules (shift doesn't exist yet)
  // EF-RULE-TASKDEFINITION-BINDING: Pass exact resolutions to proceedWithShiftCreation
  const handleResolutionModalSuccess = async (result) => {
    // If we have pending shift data, create the shift now with rule resolutions
    if (pendingShiftData) {
      try {
        // Create the shift with rule supplements using EXACT TaskDefinition IDs
        await proceedWithShiftCreation(pendingShiftData, pendingKeepOpen, result?.resolutions);
        
        const executed = result?.executed?.length || 0;
        const removed = result?.removed?.length || 0;
        
        if (executed > 0 && removed > 0) {
          toast.success(`Shift created: ${executed} rule${executed > 1 ? "s" : ""} applied, ${removed} skipped`);
        } else if (executed > 0) {
          toast.success(`Shift created with ${executed} required supplement${executed > 1 ? "s" : ""}`);
        }
      } catch (err) {
        toast.error(`Failed to create shift: ${extractErrorMessage(err)}`);
      }
    } else {
      // Existing shift edit path - supplements already created by modal
      const executed = result?.executed?.length || 0;
      const removed = result?.removed?.length || 0;
      
      if (executed > 0) {
        toast.success(`${executed} supplement${executed > 1 ? "s" : ""} updated`);
      }
      await loadData();
    }
    
    setResolutionModalOpen(false);
    setFiredRules([]);
    setResolutionShiftId(null);
    setResolutionShiftData({});
    setPendingShiftData(null);
    setPendingKeepOpen(false);
    setPendingRuleResolutions(null);
  };

  // EF-DUPLICATION-PRECREATE-UX-ADJUSTMENT v1.1: Handle duplication modal decisions
  // Called when user chooses "Duplicate Shift" in pre-create modal
  const handleDuplicationConfirm = (dates) => {
    // Store dates for later execution (after shift is saved)
    setDuplicationPlan({ dates });
    setDuplicationModalOpen(false);
    
    // Now open the shift form
    if (pendingShiftOpen) {
      setPendingShiftOpen(false);
      setShiftDialogOpen(true);
    }
  };

  // Called when user chooses "Single Shift" in pre-create modal
  const handleDuplicationSingleShift = () => {
    // Clear any previous plan
    setDuplicationPlan(null);
    setDuplicationModalOpen(false);
    
    // Open the shift form
    if (pendingShiftOpen) {
      setPendingShiftOpen(false);
      setShiftDialogOpen(true);
    }
  };

  // Called when user cancels the modal entirely
  const handleDuplicationClose = () => {
    setDuplicationPlan(null);
    setDuplicationModalOpen(false);
    setPendingShiftOpen(false);
  };

  const handleDeleteShift = async () => {
    // PLATFORM LAW: Strict validation before API call
    const reason = deletionReason?.trim() || "";
    
    if (reason.length < 10) {
      toast.error("Deletion reason must be at least 10 characters");
      return;
    }
    
    setDeletionLoading(true);
    try {
      const result = await deleteShift(selectedShift.id, reason);
      const { tasks_deleted, assignments_deleted } = result.data;
      
      let message = "Shift deleted successfully";
      if (tasks_deleted > 0 || assignments_deleted > 0) {
        const parts = [];
        if (tasks_deleted > 0) parts.push(`${tasks_deleted} task${tasks_deleted > 1 ? "s" : ""}`);
        if (assignments_deleted > 0) parts.push(`${assignments_deleted} assignment${assignments_deleted > 1 ? "s" : ""}`);
        message = `Shift deleted (removed ${parts.join(" and ")})`;
      }
      
      toast.success(message);
      setDeleteDialogOpen(false);
      setSelectedShift(null);
      setDeletionImpact(null);
      setDeletionReason("");
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeletionLoading(false);
    }
  };
  
  // Open delete dialog with impact analysis
  const handleOpenDeleteDialog = async (shift) => {
    setSelectedShift(shift);
    setDeletionReason("");
    setDeletionImpact(null);
    setDeleteDialogOpen(true);
    
    try {
      const response = await getShiftDeletionImpact(shift.id);
      setDeletionImpact(response.data);
    } catch (err) {
      console.error("Failed to get deletion impact:", err);
      // Still allow deletion even if impact fetch fails
      setDeletionImpact({ error: true, warning_message: "Unable to analyze dependencies." });
    }
  };

  // Check if Crew Leader rule was triggered
  const hasCrewLeaderRule = useMemo(() => {
    return rulePrompts.some(r => r.rule_id === "BUILTIN_CREW_LEADER");
  }, [rulePrompts]);

  // Rule handlers
  const handleRuleAcceptAll = async (acceptances) => {
    if (!pendingShift) return;
    setRuleLoading(true);
    try {
      await recordShiftRuleAcceptances(pendingShift.id, acceptances);
      const taskCount = pendingTasks.length;
      const message = taskCount > 0
        ? `Shift saved with ${taskCount} task${taskCount > 1 ? "s" : ""} and rules accepted`
        : "Shift saved with rules accepted";
      toast.success(message);
      
      // Check if Crew Leader rule was triggered - offer to create Crew Chief shift
      if (hasCrewLeaderRule) {
        setCrewLeaderShiftData({
          ...pendingShift,
          name: "Crew Chief - " + pendingShift.name,
          staff_required: 1,
          notes: `Crew Chief for shift: ${pendingShift.name}`,
        });
        setCrewLeaderDialogOpen(true);
      }
      
      setRulePromptOpen(false);
      setPendingShift(null);
      setPendingTasks([]);
      setRulePrompts([]);
      setShiftTasks([]);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setRuleLoading(false);
    }
  };

  const handleRuleOverride = async (acceptances) => {
    if (!pendingShift) return;
    setRuleLoading(true);
    try {
      await recordShiftRuleAcceptances(pendingShift.id, acceptances);
      const taskCount = pendingTasks.length;
      const message = taskCount > 0
        ? `Shift saved with ${taskCount} task${taskCount > 1 ? "s" : ""} and rules overridden`
        : "Shift saved with rules overridden";
      toast.success(message);
      setRulePromptOpen(false);
      setPendingShift(null);
      setPendingTasks([]);
      setRulePrompts([]);
      setShiftTasks([]);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setRuleLoading(false);
    }
  };

  const handleRuleCancel = () => {
    const taskCount = pendingTasks.length;
    const message = taskCount > 0
      ? `Shift created with ${taskCount} task${taskCount > 1 ? "s" : ""} - rule acknowledgment pending`
      : "Shift created - rule acknowledgment pending";
    toast.info(message);
    setRulePromptOpen(false);
    setPendingShift(null);
    setPendingTasks([]);
    setRulePrompts([]);
    setShiftTasks([]);
    loadData();
  };

  // Create Crew Chief shift
  const handleCreateCrewLeaderShift = async () => {
    if (!crewLeaderShiftData) return;
    try {
      const data = {
        project_id: id,
        venue_id: crewLeaderShiftData.venue_id,
        venue_location_id: crewLeaderShiftData.venue_location_id,
        name: crewLeaderShiftData.name,
        date: crewLeaderShiftData.date,
        start_time: crewLeaderShiftData.start_time,
        end_time: crewLeaderShiftData.end_time,
        duration_hours: crewLeaderShiftData.duration_hours || 8,
        staff_required: 1,
        status: "draft",
        notes: crewLeaderShiftData.notes,
      };
      // EVENTSFLO-CALENDAR-CREATE-SHIFT-WITH-RULES-R2-FIX-V1
      // Rule-aware create so the new Crew Chief shift gets Meal /
      // Crew Chief supplements applied immediately if its duration
      // and staff_required cross the deterministic thresholds.
      await createShiftWithRulesAutoApply(data);
      toast.success("Crew Chief shift created - you can now edit and assign the crew chief");
      setCrewLeaderDialogOpen(false);
      setCrewLeaderShiftData(null);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const handleSkipCrewLeaderShift = () => {
    setCrewLeaderDialogOpen(false);
    setCrewLeaderShiftData(null);
  };

  // Quote generation
  const handleOpenQuoteDialog = () => {
    const today = new Date();
    const validUntil = new Date(today);
    validUntil.setDate(validUntil.getDate() + 30);

    // EF-QUOTE-TAX-FIELD-FORCE-REMOVAL-V2: tax_rate removed - backend uses tenant settings
    setQuoteForm({
      issue_date: today.toISOString().split("T")[0],
      valid_until: validUntil.toISOString().split("T")[0],
      notes: "",
      // EVENTSFLO-PDF-VAT-LINE-LABEL-TOGGLE-V1 / -DEFAULT-OFF-V1
      show_line_tax_labels_on_pdf: false,
    });
    setQuoteDialogOpen(true);
  };

  const handleGenerateQuote = async (e) => {
    e.preventDefault();
    if (shifts.length === 0) {
      toast.error("Cannot generate quote: no shifts in this project");
      return;
    }

    // EVENTSFLO-TAX-CLASS-DEFAULTS-AND-GENERATION-PREFLIGHT-V1
    // Server-guard mirror of the button's disabled logic — never call the
    // quote generation endpoint when the preflight has flagged a serious
    // problem. This closes the loop if the disabled attribute is bypassed.
    if (preflightSummary.aggregator_error) {
      toast.error(
        "Tax preflight could not be calculated safely. Please resolve this before generating the quote."
      );
      return;
    }
    if (
      preflightSummary.manual_review_count > 0 &&
      taxOverrides.blockOnManualReview
    ) {
      toast.error(
        `Manual-review tax lines exist (${preflightSummary.manual_review_count}). Review or turn off the block only if authorised.`
      );
      return;
    }
    if (
      preflightSummary.manual_review_count > 0 &&
      !taxOverrides.blockOnManualReview &&
      !(taxOverrides.overrideReason || "").trim()
    ) {
      toast.error(
        "An override reason is required when the manual-review block is turned off."
      );
      return;
    }

    setQuoteLoading(true);
    try {
      // EF-QUOTE-TAX-FIELD-FORCE-REMOVAL-V2: tax_rate removed - backend uses tenant settings
      // EVENTSFLO-QUOTE-PREFLIGHT-SERVER-SIDE-GUARD-V1: pass the preflight
      // override toggle + reason so the backend mirror-guard applies the
      // same policy as the frontend banner.
      const response = await generateQuoteFromProject({
        project_id: id,
        issue_date: quoteForm.issue_date,
        valid_until: quoteForm.valid_until,
        notes: quoteForm.notes || null,
        block_on_manual_review: !!taxOverrides.blockOnManualReview,
        tax_preflight_override_reason:
          taxOverrides.blockOnManualReview
            ? null
            : (taxOverrides.overrideReason || "").trim() || null,
        // EVENTSFLO-PDF-VAT-LINE-LABEL-TOGGLE-V1 / -DEFAULT-OFF-V1
        // Send the explicit checkbox state — `=== true` so an
        // undefined / null value on the form defaults to false to
        // match the backend default and the checkbox unchecked state.
        show_line_tax_labels_on_pdf: quoteForm.show_line_tax_labels_on_pdf === true,
      });

      toast.success(`Quote ${response.data.quote_number} generated successfully`);
      setQuoteDialogOpen(false);
      navigate("/quotes");
    } catch (err) {
      // EVENTSFLO-QUOTE-PREFLIGHT-SERVER-SIDE-GUARD-V1
      // Surface the backend guard error_code with the same wording the
      // frontend banner uses.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code) {
        const testIdMap = {
          tax_preflight_aggregator_error: "quote-generate-backend-preflight-error",
          tax_preflight_manual_review_blocked: "quote-generate-backend-manual-review-error",
          tax_preflight_override_reason_required: "quote-generate-backend-manual-review-error",
        };
        toast.error(detail.message || "Tax preflight blocked quote generation.", {
          id: testIdMap[detail.error_code] || undefined,
        });
      } else {
        toast.error(extractErrorMessage(err));
      }
    } finally {
      setQuoteLoading(false);
    }
  };

  // EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1
  // Project-scoped Passport / ID export. Admin-only. Browser download.
  const handleExportPassportIdDetails = async () => {
    if (!project?.id) return;
    setPassportExportLoading(true);
    try {
      const res = await downloadProjectPassportExport(project.id);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const today = new Date().toISOString().slice(0, 10);
      const safeName = (project.name || "project").replace(/[\\/]/g, "-");
      const filename = `${safeName}-passport-id-details-${today}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setPassportExportConfirmOpen(false);
      toast.success("Passport / ID export downloaded");
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to export Passport / ID details");
    } finally {
      setPassportExportLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!project) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="Project not found"
        description="The project you're looking for doesn't exist."
        action={
          <Button onClick={() => navigate("/projects")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Projects
          </Button>
        }
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Back Navigation */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/projects")}
          className="text-muted-foreground hover:text-foreground"
          data-testid="back-to-projects-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Projects
        </Button>
      </div>

      {/* Project Header */}
      <div className="bg-card/50 border border-border rounded-xl p-6" data-testid="project-header">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <ClipboardList className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground" data-testid="project-name">
                  {project.name}
                </h1>
                <p className="text-muted-foreground flex items-center gap-2 mt-1">
                  <Building2 className="h-4 w-4" />
                  <span data-testid="project-client">{client?.name || "No client"}</span>
                </p>
              </div>
            </div>

            {project.description && (
              <p className="text-muted-foreground text-sm max-w-xl">{project.description}</p>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm">
              <StatusBadge status={project.status} />
              {project.start_date && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Start: {formatDate(project.start_date)}
                </span>
              )}
              {project.end_date && (
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  End: {formatDate(project.end_date)}
                </span>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleOpenShiftDialog}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="add-shift-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Shift
            </Button>
            {/* EVENTSFLO-PROJECT-DETAIL-OPEN-IN-PLANNER-BUTTON-V1
                Deep-links into the active Sheet Crew Planner with this
                project preselected. Confirmed safe target per the
                EVENTSFLO-ACTIVE-CREW-PLANNER-ROUTE-AUDIT-BEFORE-DEEPLINK-V1
                audit. */}
            <Button
              asChild
              variant="outline"
              className="border-border text-secondary-foreground hover:bg-accent"
              data-testid="open-in-planner-btn"
            >
              <a
                href={`/planner/sheet?project_id=${project.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <TableProperties className="h-4 w-4 mr-2" />
                Open in Planner
              </a>
            </Button>
            <Button
              onClick={handleOpenQuoteDialog}
              variant="outline"
              className="border-border text-secondary-foreground hover:bg-accent"
              data-testid="generate-quote-btn"
            >
              <FileText className="h-4 w-4 mr-2" />
              Generate Quote
            </Button>
            {/* EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1 */}
            <Button
              onClick={() => setPassportExportConfirmOpen(true)}
              variant="outline"
              className="border-border text-secondary-foreground hover:bg-accent"
              data-testid="export-passport-id-btn"
            >
              <BookUser className="h-4 w-4 mr-2" />
              Export Passport / ID Details
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="project-stats">
        <div className="bg-card/50 border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Clock className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground" data-testid="total-shifts">
                {stats.totalShifts}
              </p>
              <p className="text-sm text-muted-foreground">Total Shifts</p>
            </div>
          </div>
        </div>

        <div className="bg-card/50 border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/10 rounded-lg">
              <Users className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground" data-testid="total-staff">
                {stats.totalStaffRequired}
              </p>
              <p className="text-sm text-muted-foreground">Crew Required</p>
            </div>
          </div>
        </div>

        <div className="bg-card/50 border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/10 rounded-lg">
              <Clock className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground" data-testid="total-hours">
                {stats.totalHours.toFixed(1)}h
              </p>
              <p className="text-sm text-muted-foreground">Total Hours</p>
            </div>
          </div>
        </div>
      </div>

      {/* Shifts Section */}
      <div className="bg-card/50 border border-border rounded-xl overflow-hidden" data-testid="shifts-section">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Project Shifts ({shifts.length})
          </h2>
          
          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <Button
              variant={shiftViewMode === "table" ? "default" : "ghost"}
              size="sm"
              onClick={() => setShiftViewMode("table")}
              className="h-8"
              data-testid="view-mode-table"
            >
              <LayoutList className="h-4 w-4 mr-1" />
              Edit Shift 1
            </Button>
            <Button
              variant={shiftViewMode === "planner" ? "default" : "ghost"}
              size="sm"
              onClick={() => setShiftViewMode("planner")}
              className="h-8"
              data-testid="view-mode-planner"
            >
              <LayoutGrid className="h-4 w-4 mr-1" />
              Edit Shift 2
            </Button>
          </div>
        </div>

        {shifts.length === 0 ? (
          <div className="p-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground mb-4">No shifts added to this project yet</p>
            <Button
              onClick={handleOpenShiftDialog}
              className="bg-primary hover:bg-primary/90"
              data-testid="add-first-shift-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Shift
            </Button>
          </div>
        ) : shiftViewMode === "planner" ? (
          /* Edit Shift 2 - Planner View */
          <div className="h-[600px]">
            <ShiftPlannerView
              projectId={id}
              shifts={shifts.map(s => ({
                ...s,
                venue_name: getVenueName(s.venue_id),
                venue_location_name: getVenueLocationName(s.venue_location_id),
                project_status: project?.status // EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Include project status
              }))}
              projectStatus={project?.status} // EF-PLANNER-VISIBILITY-AND-OVERRIDE-FIX-V2: Pass project status
              onEditShift={handleEditShift}
              onDeleteShift={handleOpenDeleteDialog}
              onCloneShift={async (shift) => {
                try {
                  await duplicateShift(shift.id);
                  toast.success("Shift cloned successfully");
                  loadData();
                } catch (err) {
                  toast.error(extractErrorMessage(err));
                }
              }}
              onRefresh={loadData}
              formatTime={formatTime}
              formatDuration={formatDuration}
            />
          </div>
        ) : (
          /* Table View (Edit Shift 1) */
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="shifts-table">
              <thead className="bg-surface-elevated/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Shift Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Venue
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Crew
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {shifts.map((shift) => (
                  <tr key={shift.id} className="hover:bg-accent/30" data-testid={`shift-row-${shift.id}`}>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditShift(shift)}
                          className="text-left font-medium text-foreground hover:text-primary hover:underline focus:outline-none"
                          data-testid={`shift-name-link-${shift.id}`}
                        >
                          {shift.name}
                        </button>
                        {/* EVENTSFLO-SHIFT-RULE-STATUS-SHIELD-NON-BLOCKING-V1 */}
                        <ShiftRuleStatusBadge
                          shiftId={shift.id}
                          compact
                          onReview={() => handleEditShift(shift)}
                          refreshKey={shiftBadgeRefreshKey}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-foreground">
                      {getVenueName(shift.venue_id)}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-foreground">
                      {getVenueLocationName(shift.venue_location_id)}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-foreground">
                      {formatDate(shift.date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-foreground">
                      {shift.start_time} - {shift.end_time}
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-foreground">
                      {shift.duration_hours?.toFixed(1)}h
                    </td>
                    <td className="px-4 py-3 text-sm text-secondary-foreground">
                      {shift.staff_required}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={shift.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDuplicateShift(shift)}
                          className="text-primary hover:text-primary hover:bg-primary/10"
                          title="Duplicate shift"
                          data-testid={`duplicate-shift-${shift.id}`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditShift(shift)}
                          className="text-muted-foreground hover:text-foreground hover:bg-accent"
                          data-testid={`edit-shift-${shift.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDeleteDialog(shift)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          data-testid={`delete-shift-${shift.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add Shift Dialog */}
      <Dialog
        open={shiftDialogOpen}
        onOpenChange={(next) => {
          // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
          // Intercept any close attempt (X / overlay / Esc). If the
          // user has touched supplements/line items since the modal
          // opened, ask before discarding so we can inverse-undo the
          // immediate persists.
          if (next) {
            setShiftDialogOpen(true);
            return;
          }
          if (editingShift && shiftLineItemsRef.current?.hasChanges?.()) {
            setDiscardConfirmOpen(true);
            return;
          }
          setShiftDialogOpen(false);
        }}
      >
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Plus className="h-5 w-5 text-primary" />
              {editingShift ? 'Edit Shift' : 'Add Shift to Project'}
              {/* EVENTSFLO-SHIFT-RULE-STATUS-SHIELD-NON-BLOCKING-V1
                  Only meaningful for an existing shift — new shifts have no
                  persisted state to evaluate. */}
              {editingShift && (
                <ShiftRuleStatusBadge
                  shiftId={editingShift.id}
                  compact={false}
                  refreshKey={shiftBadgeRefreshKey}
                  onLoaded={setEditingShiftRuleStatus}
                  className="ml-2"
                />
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Rule Auto-Supplement Visibility Message */}
          {ruleAddedSupplements.length > 0 && (
            <div 
              className="p-3 rounded-lg border-l-4 bg-surface-elevated border-primary"
              data-testid="rule-supplement-message"
            >
              {ruleAddedSupplements.length === 1 ? (
                <p className="text-sm text-foreground">
                  Based on active rules, a <span className="font-medium text-foreground">{ruleAddedSupplements[0]}</span> has been added.
                </p>
              ) : (
                <div className="text-sm text-foreground">
                  <p className="mb-2">Based on active rules, the following supplements were added:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {ruleAddedSupplements.map((name, idx) => (
                      <li key={idx} className="text-foreground font-medium">{name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          <form onSubmit={(e) => handleShiftSubmit(e, false)} className="space-y-4">
            {/* Basic Shift Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <Label htmlFor="shift-name" className="text-secondary-foreground">Shift Name *</Label>
                <Input
                  id="shift-name"
                  value={shiftForm.name}
                  onChange={(e) => setShiftForm({ ...shiftForm, name: e.target.value })}
                  placeholder="e.g., Morning Shift"
                  required
                  className="bg-input border-input text-foreground"
                  data-testid="shift-name-input"
                />
              </div>
            </div>

            {/* EF-SHIFT-MULTI-DAY-DUPLICATION v1.0: Venue + Location on same row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="shift-venue" className="text-secondary-foreground">Venue *</Label>
                <Select
                  value={shiftForm.venue_id}
                  onValueChange={(value) => {
                    // CASCADING DROPDOWN: Reset venue_location_id when venue changes
                    setShiftForm(prev => ({ 
                      ...prev, 
                      venue_id: value,
                      venue_location_id: "" // Reset - no stale venue location from previous venue
                    }));
                  }}
                >
                  <SelectTrigger className="bg-input border-input text-foreground" data-testid="shift-venue-select">
                    <SelectValue placeholder="Select venue" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* EVENTSFLO-CLIENT-SCOPED-VENUE-LOCATION-FILTERING-V1
                        Shift venues are scoped to the Project's client
                        (owned / shared / client-copy). Legacy shifts
                        whose venue is no longer visible for the client
                        keep their selected value in the form model but
                        the dropdown surfaces only client-visible
                        venues. */}
                    {filterVenuesForClient(venues, project?.client_id).map((venue) => (
                      <SelectItem key={venue.id} value={venue.id}>
                        {venue.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {shiftValidationErrors.venue && (
                  <p className="text-xs text-destructive mt-1">{shiftValidationErrors.venue}</p>
                )}
              </div>
              <div>
                <Label htmlFor="shift-venue-location" className="text-secondary-foreground">Location *</Label>
                <Select
                  value={shiftForm.venue_location_id}
                  onValueChange={(value) => setShiftForm(prev => ({ ...prev, venue_location_id: value }))}
                  disabled={!shiftForm.venue_id}
                >
                  <SelectTrigger 
                    className={`bg-input border-input text-foreground ${!shiftForm.venue_id ? 'opacity-50' : ''}`} 
                    data-testid="shift-venue-location-select"
                  >
                    <SelectValue placeholder={shiftForm.venue_id ? "Select location" : "Select a venue first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getFilteredVenueLocations(shiftForm.venue_id).map((vl) => (
                      <SelectItem key={vl.id} value={vl.id}>
                        {vl.name} {vl.desc_short ? `- ${vl.desc_short}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {shiftValidationErrors.venue_location && (
                  <p className="text-xs text-destructive mt-1">{shiftValidationErrors.venue_location}</p>
                )}
                {shiftForm.venue_id && getFilteredVenueLocations(shiftForm.venue_id).length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">No locations defined for this venue</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="shift-date" className="text-secondary-foreground">Date *</Label>
                <DatePicker
                  value={shiftForm.date}
                  onChange={(value) => handleShiftFieldChange('date', value)}
                  placeholder="Select date"
                  minDate={project?.start_date}
                  maxDate={project?.end_date}
                  className={shiftValidationErrors.date ? 'border-destructive' : ''}
                  data-testid="shift-date-input"
                />
                {shiftValidationErrors.date && (
                  <p className="text-xs text-destructive mt-1">{shiftValidationErrors.date}</p>
                )}
              </div>
              {/* EF-SHIFT-STAFFING-FIELD-PARAMETERIZATION: Combined Crew Required control */}
              <div>
                <Label htmlFor="shift-staff" className="text-secondary-foreground">Crew Required *</Label>
                <div className="flex gap-2">
                  <Input
                    id="shift-staff"
                    type="number"
                    min="1"
                    value={shiftForm.staff_required}
                    onChange={(e) => handleShiftFieldChange('staff_required', e.target.value)}
                    required
                    className="bg-input border-input text-foreground w-20"
                    data-testid="shift-staff-input"
                  />
                  <Select
                    value={shiftForm.primary_role_id}
                    onValueChange={(value) => setShiftForm({ ...shiftForm, primary_role_id: value })}
                  >
                    <SelectTrigger 
                      className="flex-1 bg-input border-input text-foreground"
                      data-testid="shift-role-select"
                    >
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffingRoles.map((role) => (
                        <SelectItem key={role.id} value={role.id}>
                          <div className="flex items-center justify-between gap-2">
                            <span>{role.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatCurrency(role.default_charge_rate || 0)}/hr
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="shift-start" className="text-secondary-foreground">Start Time *</Label>
                <TimePicker
                  value={shiftForm.start_time}
                  onChange={handleStartTimeChange}
                  placeholder="HH:MM"
                  data-testid="shift-start-input"
                />
              </div>
              <div>
                <Label htmlFor="shift-duration" className="text-secondary-foreground">Duration (hrs) *</Label>
                <Input
                  id="shift-duration"
                  type="number"
                  min="0.5"
                  max="24"
                  step="0.5"
                  value={shiftForm.duration}
                  onChange={(e) => handleDurationChange(e.target.value)}
                  placeholder="e.g. 8"
                  className={`bg-input border-input text-foreground ${shiftValidationErrors.duration ? 'border-destructive' : ''}`}
                  data-testid="shift-duration-input"
                />
                {shiftValidationErrors.duration && (
                  <p className="text-xs text-destructive mt-1">{shiftValidationErrors.duration}</p>
                )}
              </div>
              <div>
                <Label htmlFor="shift-end" className="text-secondary-foreground">End Time</Label>
                <TimePicker
                  value={shiftForm.end_time}
                  onChange={(value) => handleShiftFieldChange('end_time', value)}
                  placeholder="Auto"
                  disabled={true}
                  data-testid="shift-end-input"
                />
                <p className="text-xs text-muted-foreground mt-1">Derived from start + duration</p>
              </div>
            </div>
            
            {/* Overnight indicator */}
            {isOvernightShift && (
              <div className="flex items-center gap-2 p-2 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                <Moon className="h-4 w-4 text-purple-400" />
                <span className="text-sm text-purple-300">Overnight shift (crosses midnight)</span>
              </div>
            )}
            
            {/* Out of Town Toggle - Prominent Button Style */}
            <div className="flex items-center justify-between p-4 bg-surface-base rounded-lg border border-border/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <MapPin className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <Label htmlFor="shift-out-of-town" className="text-sm font-semibold text-foreground cursor-pointer">
                    Out of Town (OOT)
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Add travel supplements (crew time, distance, tolls)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* OOT Toggle Button */}
                <Button
                  type="button"
                  variant={shiftForm.out_of_town ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleOOTToggle(!shiftForm.out_of_town)}
                  className={shiftForm.out_of_town ? "bg-primary text-primary-foreground" : ""}
                  data-testid="shift-out-of-town-btn"
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  {shiftForm.out_of_town ? "OOT Enabled" : "Enable OOT"}
                </Button>
                {/* Travel Advisor Button - only when OOT enabled */}
                {shiftForm.out_of_town && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleOpenTravelAdvisor}
                    className="text-xs"
                    data-testid="travel-advisor-btn"
                  >
                    <Car className="h-3 w-3 mr-1" />
                    Travel Advisor
                  </Button>
                )}
              </div>
            </div>
            
            {/* Inline Task Selection - Crew/role tasks under "Tasks for this Shift" */}
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="text-secondary-foreground flex items-center gap-2">
                  <ListChecks className="h-4 w-4 text-primary" />
                  Tasks for this Shift
                </Label>
                <span className="text-xs text-muted-foreground">
                  {shiftTasks.filter(t => (t.quantity > 0 || t.persisted) && !isSupplementShiftTask(t)).length} task{shiftTasks.filter(t => (t.quantity > 0 || t.persisted) && !isSupplementShiftTask(t)).length !== 1 ? "s" : ""} selected
                </span>
              </div>

              {/* Task Selection - Simple Dropdown (CREW/ROLE tasks only) */}
              {/* EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
                  Filter out supplement/item task defs so the operator
                  can't accidentally add them here — the Associated Costs
                  section below is the dedicated entry point. */}
              <Select onValueChange={handleAddTask}>
                <SelectTrigger 
                  className="bg-input border-input text-foreground" 
                  data-testid="add-task-select"
                >
                  <SelectValue placeholder="+ Add a task to this shift..." />
                </SelectTrigger>
                <SelectContent>
                  {taskDefinitions
                    .filter(t => t.is_active && !shiftTasks.find(st => st.task_definition_id === t.id))
                    .filter(t => !supplementTaskDefs.find((s) => s.id === t.id))
                    .map((task) => (
                      <SelectItem key={task.id} value={task.id}>
                        {task.name}
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>

              {/* Selected Tasks List - CREW/ROLE rows only */}
              {shiftTasks.filter(t => (t.quantity > 0 || t.duration_hours > 0 || t.persisted) && !isSupplementShiftTask(t)).length > 0 && (
                <div className="space-y-2 max-h-[250px] overflow-y-auto bg-input/30 rounded-lg p-2">
                  {shiftTasks
                    .filter(t => (t.quantity > 0 || t.duration_hours > 0 || t.persisted) && !isSupplementShiftTask(t))
                    .map((task) => {
                      const isTravelTask = task.supplement_group === "travel" || task.is_travel_task;
                      // EF-HOURLY-TASK-UI-BINDING-FIX: Detect hourly items
                      const isHourlyItem = task.charge_unit === "hourly" || (task.duration_hours && task.duration_hours > 0);
                      
                      // Calculate total based on item type
                      const qty = parseFloat(task.quantity) || 1;
                      const rate = parseFloat(task.charge_rate) || 0;
                      const hours = parseFloat(task.duration_hours) || 0;
                      const total = isHourlyItem ? (rate * qty * hours) : (rate * qty);
                      
                      // Format breakdown string
                      const formatBreakdown = () => {
                        if (isHourlyItem && hours > 0) {
                          return `${formatCurrency(rate)} × ${qty} × ${hours}h = ${formatCurrency(total)}`;
                        } else if (qty > 1) {
                          return `${formatCurrency(rate)} × ${qty} = ${formatCurrency(total)}`;
                        }
                        return `${formatCurrency(rate)}/${task.charge_unit || 'fixed'} = ${formatCurrency(total)}`;
                      };
                      
                      return (
                        <div
                          key={task.task_definition_id}
                          className={`flex items-center gap-3 p-3 rounded-lg border ${
                            isTravelTask 
                              ? 'bg-blue-500/10 border-blue-500/30' 
                              : 'bg-secondary/50 border-border'
                          }`}
                          data-testid={`selected-task-${task.task_definition_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {isTravelTask && <Car className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />}
                              <p className="text-sm font-medium text-foreground truncate">
                                {task.task_name}
                              </p>
                            </div>
                            {/* EF-TASKITEM-COST-BREAKDOWN-DISPLAY: Show pricing breakdown */}
                            <p className="text-xs text-muted-foreground">
                              {formatBreakdown()}
                            </p>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {/* EF-HOURLY-TASK-UI-BINDING-FIX: Bind to duration_hours for hourly, quantity for fixed */}
                            {isHourlyItem ? (
                              <>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.5"
                                  value={task.duration_hours ?? ""}
                                  onChange={(e) => handleUpdateTaskDuration(task.task_definition_id, e.target.value)}
                                  className="w-16 h-8 bg-input border-input text-foreground text-center text-sm"
                                  data-testid={`task-duration-${task.task_definition_id}`}
                                />
                                <span className="text-xs text-muted-foreground">hrs</span>
                              </>
                            ) : (
                              <>
                                <Input
                                  type="number"
                                  min="0"
                                  step={isTravelTask ? "0.5" : "1"}
                                  value={task.quantity ?? ""}
                                  onChange={(e) => handleUpdateTaskQuantity(task.task_definition_id, e.target.value)}
                                  className="w-16 h-8 bg-input border-input text-foreground text-center text-sm"
                                  data-testid={`task-quantity-${task.task_definition_id}`}
                                />
                                <span className="text-xs text-muted-foreground">qty</span>
                              </>
                            )}
                            
                            {/* Editable Unit Price (for travel supplements) */}
                            {isTravelTask && (
                              <>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={task.charge_rate ?? ""}
                                  onChange={(e) => handleUpdateTaskRate(task.task_definition_id, e.target.value)}
                                  className="w-20 h-8 bg-input border-input text-foreground text-center text-sm"
                                  data-testid={`task-rate-${task.task_definition_id}`}
                                />
                                <span className="text-xs text-muted-foreground">rate</span>
                              </>
                            )}
                          </div>
                          
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTask(task.task_definition_id)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid={`remove-task-${task.task_definition_id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                </div>
              )}

              {shiftTasks.filter(t => (t.quantity > 0 || t.duration_hours > 0 || t.persisted) && !isSupplementShiftTask(t)).length === 0 && (
                <p className="text-sm text-muted-foreground italic text-center py-2">
                  No tasks added yet. Select tasks above to include them in this shift.
                </p>
              )}
            </div>

            {/* EVENTSFLO-SHIFT-SUPPLEMENT-CREATE-EDIT-QUANTITY-FIX-V1
                Associated Costs / Supplements — independent of Crew
                Required quantity. Default qty 1, editable, never auto-
                derived from staff_required.
                Rendered only in CREATE mode — in Edit mode the
                ShiftLineItems block below already exposes its own
                "Add Supplement" entry point, so showing this picker
                too would result in two visually-similar headings. */}
            {!editingShift && (
            <div className="space-y-3 pt-2 border-t border-border" data-testid="shift-supplements-section">
              <div className="flex items-center justify-between">
                <Label className="text-secondary-foreground flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-primary" />
                  Associated Costs, Credits &amp; Adjustments
                </Label>
                <span className="text-xs text-muted-foreground">
                  {shiftTasks.filter((t) => isSupplementShiftTask(t) && (t.quantity > 0 || t.persisted)).length} item{shiftTasks.filter((t) => isSupplementShiftTask(t) && (t.quantity > 0 || t.persisted)).length !== 1 ? "s" : ""} selected
                </span>
              </div>
              {/* EVENTSFLO-QUOTE-CREDIT-LINE-AUDIT-AND-GENERIC-ADJUSTMENT-V1
                  Existing infrastructure already supports credits/
                  adjustments via Task Definitions with a negative
                  default_charge_rate (e.g. "Geste Commercial" -36.75,
                  "iCrew Discount" -100, "AVOIR" -540, "iCrew Remise"
                  -33.75). Adding one to a shift produces
                  `line_total = qty × multiplier × unit_rate` which flows
                  through the quote view-model as a negative line and
                  reduces the subtotal / total without touching crew
                  count, Crew Chief, Meal or OOT rules. This hint text
                  makes the flow discoverable — no backend contract
                  change. Both quantity and unit_rate remain editable
                  after selection so the operator can trim to the exact
                  credit amount (e.g. 4 crew × 2h × -36.75 = -294.00). */}
              <p className="text-xs text-muted-foreground" data-testid="shift-credit-adjustment-hint">
                Add associated costs (e.g. Prime Poids-Lourd), credits or
                commercial gestures (e.g. Geste Commercial, iCrew Discount,
                AVOIR). Pick a Task Definition, then edit quantity /
                unit rate inline on the line-items table below to match
                the exact credit amount.
              </p>

              <Select onValueChange={handleAddSupplementTask}>
                <SelectTrigger
                  className="bg-input border-input text-foreground"
                  data-testid="add-supplement-select"
                >
                  <SelectValue placeholder="+ Add supplement, credit or adjustment..." />
                </SelectTrigger>
                <SelectContent>
                  {supplementTaskDefs
                    .filter((t) => !shiftTasks.find((st) => st.task_definition_id === t.id))
                    .map((task) => (
                      <SelectItem key={task.id} value={task.id}>
                        {task.name}
                      </SelectItem>
                    ))}
                  {supplementTaskDefs.filter((t) => !shiftTasks.find((st) => st.task_definition_id === t.id)).length === 0 && (
                    <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                      No more supplements available
                    </div>
                  )}
                </SelectContent>
              </Select>

              {shiftTasks.filter((t) => isSupplementShiftTask(t) && (t.quantity > 0 || t.duration_hours > 0 || t.persisted)).length > 0 && (
                <div className="space-y-2 max-h-[250px] overflow-y-auto bg-input/30 rounded-lg p-2">
                  {shiftTasks
                    .filter((t) => isSupplementShiftTask(t) && (t.quantity > 0 || t.duration_hours > 0 || t.persisted))
                    .map((task) => {
                      // EVENTSFLO-SHIFT-SUPPLEMENT-HOURLY-CALC-EDIT-STABILITY-V2
                      // Read the LIVE shift form duration at render
                      // time. The previously-captured task.duration_hours
                      // was set once (at the moment the supplement was
                      // added) and went stale when the operator edited
                      // the shift's Duration field afterwards — this
                      // surfaced as the "1 × Prime Poids-Lourd × €8.75 =
                      // €8.75" symptom (€8.75 instead of €105 on a 12h
                      // shift).
                      const liveShiftHours = parseFloat(shiftForm?.duration) || 0;
                      const taskHours = parseFloat(task.duration_hours) || 0;
                      const chargeUnit = (task.charge_unit || "").toLowerCase();
                      const isHourlyItem = chargeUnit === "hourly"
                        || chargeUnit === "hour"
                        || chargeUnit === "per_hour"
                        || taskHours > 0
                        || liveShiftHours > 0;
                      const qty = parseFloat(task.quantity) || 1;
                      const rate = parseFloat(task.charge_rate) || 0;
                      const hours = isHourlyItem
                        ? (liveShiftHours || taskHours)
                        : 0;
                      const total = isHourlyItem ? (rate * qty * hours) : (rate * qty);
                      const breakdown = isHourlyItem && hours > 0
                        ? `${formatCurrency(rate)} × ${qty} × ${hours}h = ${formatCurrency(total)}`
                        : `${formatCurrency(rate)} × ${qty} = ${formatCurrency(total)}`;
                      return (
                        <div
                          key={task.task_definition_id}
                          className="flex items-center gap-3 p-3 rounded-lg border bg-secondary/50 border-border"
                          data-testid={`selected-supplement-${task.task_definition_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {task.task_name}
                            </p>
                            <p className="text-xs text-muted-foreground">{breakdown}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={task.quantity ?? ""}
                              onChange={(e) => handleUpdateTaskQuantity(task.task_definition_id, e.target.value)}
                              className="w-16 h-8 bg-input border-input text-foreground text-center text-sm"
                              data-testid={`supplement-quantity-${task.task_definition_id}`}
                            />
                            <span className="text-xs text-muted-foreground">qty</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveTask(task.task_definition_id)}
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            data-testid={`remove-supplement-${task.task_definition_id}`}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                </div>
              )}

              {shiftTasks.filter((t) => isSupplementShiftTask(t) && (t.quantity > 0 || t.duration_hours > 0 || t.persisted)).length === 0 && (
                <p className="text-sm text-muted-foreground italic text-center py-2">
                  No supplements, credits or adjustments added. Use the dropdown above to add associated costs (e.g. Prime Poids-Lourd) or credits (e.g. Geste Commercial, iCrew Discount) — quantity is independent of Crew Required.
                </p>
              )}
            </div>
            )}

            {/* EF-SHIFT-MEETING-PLACE-V1: Meeting Place Instructions */}
            <div>
              <Label htmlFor="shift-meeting-place" className="text-secondary-foreground">Meeting Place</Label>
              <Textarea
                id="shift-meeting-place"
                value={shiftForm.meeting_place}
                onChange={(e) => setShiftForm({ ...shiftForm, meeting_place: e.target.value })}
                placeholder="Enter meeting point instructions (e.g. Main entrance, backstage gate, dock access point)"
                className="bg-input border-input text-foreground"
                rows={2}
                maxLength={1000}
                data-testid="shift-meeting-place-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Where crew should meet on arrival
              </p>
            </div>

            {/* EVENTSFLO-SHIFT-ON-SITE-CONTACT-FROM-CLIENT-CONTACTS-V1 */}
            <div>
              <Label htmlFor="shift-on-site-contact" className="text-secondary-foreground">
                On-Site Contact
              </Label>
              {clientContacts.length === 0 ? (
                <p
                  className="text-xs text-amber-600 dark:text-amber-400 mt-1"
                  data-testid="shift-on-site-contact-empty"
                >
                  No client contacts found for this client. Add a client contact first.
                </p>
              ) : (
                <Select
                  value={shiftForm.on_site_contact_id || "__none__"}
                  onValueChange={(v) =>
                    setShiftForm({
                      ...shiftForm,
                      on_site_contact_id: v === "__none__" ? "" : v,
                    })
                  }
                >
                  <SelectTrigger
                    id="shift-on-site-contact"
                    className="bg-input border-input text-foreground"
                    data-testid="shift-on-site-contact-trigger"
                  >
                    <SelectValue placeholder="Select an on-site contact (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__" data-testid="shift-on-site-contact-option-none">
                      None
                    </SelectItem>
                    {clientContacts.map((c) => {
                      const name = `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email;
                      const role = c.role ? ` · ${c.role}` : "";
                      return (
                        <SelectItem
                          key={c.id}
                          value={c.id}
                          data-testid={`shift-on-site-contact-option-${c.id}`}
                        >
                          {name}{role}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
              {shiftForm.on_site_contact_id && clientContacts.length > 0 && (() => {
                const c = clientContacts.find((x) => x.id === shiftForm.on_site_contact_id);
                if (!c) return null;
                return (
                  <p
                    className="text-xs text-muted-foreground mt-1"
                    data-testid="shift-on-site-contact-preview"
                  >
                    {c.email}{c.mobile ? ` · ${c.mobile}` : ""}
                  </p>
                );
              })()}
            </div>

            {/* EVENTSFLO-SHIFT-RULE-SUPPLEMENT-TASK-OVERRIDE-V1
               Per-shift override of the Task Definition used by the
               Meal Allowance and Crew Chief rules. Label/task only —
               threshold lives in Live Rule Settings, price lives in
               Task Definitions / Supplements. */}
            {(() => {
              // EVENTSFLO-SHIFT-EDIT-TASK-DEFINITION-DROPDOWN-VISIBILITY-FIX-V1:
              // Drive these from `task_rule_configs` (the same source of
              // truth used by rule preview/auto-apply) instead of the
              // legacy `supplement_group` string tag. New Task Definitions
              // carry only the correct `category_id`/`subcategory_id`; they
              // do not always inherit the legacy string tag, so the old
              // filter silently hid them from this dropdown.
              const mealOptions = getTaskDefinitionsForRule("MEAL");
              const ccOptions = getTaskDefinitionsForRule("CREW_CHIEF");

              // Backward-compat: include the currently-selected supplement
              // task even if it falls outside the rule-config window, so
              // existing shifts never lose their saved selection after this
              // migration.
              const ensureSelected = (list, selectedId) => {
                if (!selectedId) return list;
                if (list.find((t) => t.id === selectedId)) return list;
                const fallback = (taskDefinitions || []).find((t) => t.id === selectedId);
                return fallback ? [...list, fallback] : list;
              };
              const mealOptionsWithSelected = ensureSelected(
                mealOptions,
                shiftForm.meal_supplement_task_definition_id,
              );
              const ccOptionsWithSelected = ensureSelected(
                ccOptions,
                shiftForm.crew_chief_supplement_task_definition_id,
              );
              // EVENTSFLO-SHIFT-RULE-SUPPRESSED-ITEM-LABEL-VISIBILITY-FIX-V1
              // Build a set of rule_ids that are currently suppressed
              // (deliberately removed by the admin) so we can flag the
              // matching rule-label selector with [Manual Override].
              const suppressedRuleIds = new Set(
                (editingShiftRuleStatus?.categories || [])
                  .filter((c) => c?.type === "suppressed_rule_item")
                  .map((c) => c.rule),
              );
              const renderRow = (label, helper, value, options, onChange, testidBase, suppressionRuleId) => {
                const isSuppressed = !!suppressionRuleId && suppressedRuleIds.has(suppressionRuleId);
                return (
                <div>
                  <Label htmlFor={testidBase} className="text-secondary-foreground flex items-center gap-2 flex-wrap">
                    <span>{label}</span>
                    {isSuppressed && (
                      <span
                        className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300"
                        data-testid={`${testidBase}-suppressed-badge`}
                      >
                        Manual Override
                      </span>
                    )}
                  </Label>
                  {options.length === 0 ? (
                    <p
                      className="text-xs text-amber-600 dark:text-amber-400 mt-1"
                      data-testid={`${testidBase}-empty`}
                    >
                      No active Task Definitions found for this supplement group. Add one in
                      Task Definitions / Supplements.
                    </p>
                  ) : (
                    <>
                      <Select
                        value={value || "__default__"}
                        onValueChange={(v) =>
                          onChange(v === "__default__" ? "" : v)
                        }
                      >
                        <SelectTrigger
                          id={testidBase}
                          className="bg-input border-input text-foreground"
                          data-testid={`${testidBase}-trigger`}
                        >
                          <SelectValue placeholder="Use default" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem
                            value="__default__"
                            data-testid={`${testidBase}-option-default`}
                          >
                            Use default (engine picks)
                          </SelectItem>
                          {options.map((t) => (
                            <SelectItem
                              key={t.id}
                              value={t.id}
                              data-testid={`${testidBase}-option-${t.id}`}
                            >
                              {t.name}
                              {t.default_charge_rate != null && (
                                <span className="text-muted-foreground">
                                  {" "}· €{Number(t.default_charge_rate).toFixed(2)}
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">{helper}</p>
                      {isSuppressed && (
                        <p
                          className="text-xs text-amber-600 dark:text-amber-400 mt-1"
                          data-testid={`${testidBase}-suppressed-helper`}
                        >
                          {suppressionRuleId === "BUILTIN_MEAL_ALLOWANCE"
                            ? "Meal Allowance was intentionally removed for this shift."
                            : "Crew Chief was intentionally removed for this shift."}
                        </p>
                      )}
                    </>
                  )}
                </div>
                );
              };
              return (
                <>
                  {renderRow(
                    "Meal Allowance — Label / Task used when rule triggers",
                    "Threshold lives in Live Rule Settings · price lives in Task Definitions / Supplements",
                    shiftForm.meal_supplement_task_definition_id,
                    mealOptionsWithSelected,
                    (v) =>
                      setShiftForm({
                        ...shiftForm,
                        meal_supplement_task_definition_id: v,
                      }),
                    "shift-meal-supplement",
                    "BUILTIN_MEAL_ALLOWANCE",
                  )}
                  {renderRow(
                    "Crew Chief — Label / Task used when rule triggers",
                    "Threshold lives in Live Rule Settings · price lives in Task Definitions / Supplements",
                    shiftForm.crew_chief_supplement_task_definition_id,
                    ccOptionsWithSelected,
                    (v) =>
                      setShiftForm({
                        ...shiftForm,
                        crew_chief_supplement_task_definition_id: v,
                      }),
                    "shift-crew-chief-supplement",
                    "BUILTIN_CREW_CHIEF",
                  )}
                </>
              );
            })()}

            {/* Notes */}
            <div>
              <Label htmlFor="shift-notes" className="text-secondary-foreground">Notes</Label>
              <Textarea
                id="shift-notes"
                value={shiftForm.notes}
                onChange={(e) => setShiftForm({ ...shiftForm, notes: e.target.value })}
                placeholder="Additional notes..."
                className="bg-input border-input text-foreground"
                rows={2}
                data-testid="shift-notes-input"
              />
            </div>

            {/* Shift Costs - Line Items (only when editing existing shift) */}
            {editingShift && (
              <div className="border-t border-border/50 pt-4 mt-4">
                <ShiftLineItems 
                  ref={shiftLineItemsRef}
                  shiftId={editingShift.id}
                  crewCount={parseInt(shiftForm.staff_required) || 1}
                  durationHours={parseFloat(shiftForm.duration) || 0}
                  onTotalsChange={() => {}}
                />
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  // EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1
                  if (editingShift && shiftLineItemsRef.current?.hasChanges?.()) {
                    setDiscardConfirmOpen(true);
                    return;
                  }
                  setShiftDialogOpen(false);
                  setEditingShift(null);
                  setShiftTasks([]);
                  setRuleAddedSupplements([]);
                }}
                className="border-border text-secondary-foreground"
                disabled={shiftSubmitting}
                data-testid="shift-dialog-cancel-btn"
              >
                {editingShift ? 'Cancel' : 'Done'}
              </Button>
              {!editingShift && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={(e) => handleShiftSubmit(e, true)}
                  className="border-primary text-primary hover:bg-primary/20"
                  disabled={shiftSubmitting}
                  data-testid="add-another-shift-btn"
                >
                  <ChevronsRight className="h-4 w-4 mr-1" />
                  Add & Another
                </Button>
              )}
              <Button
                type="submit"
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={shiftSubmitting}
                data-testid="submit-shift-btn"
              >
                {shiftSubmitting ? (
                  <span className="flex items-center gap-2">
                    <span className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
                    {editingShift ? 'Updating...' : 'Creating...'}
                  </span>
                ) : (
                  <>
                    {editingShift ? 'Update Shift' : 'Add Shift'}
                    {!editingShift && shiftTasks.length > 0 && (
                      <span className="ml-1 opacity-80">
                        ({shiftTasks.length} task{shiftTasks.length > 1 ? "s" : ""})
                      </span>
                    )}
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Generate Quote Dialog */}
      <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Generate Quote
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGenerateQuote} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will generate a quote from all shift tasks in this project.
              Line items will be snapshotted at current rates.
            </p>

            {/* EVENTSFLO-TAX-CLASS-DEFAULTS-AND-GENERATION-PREFLIGHT-V1 */}
            <QuoteTaxPreflight
              projectId={id}
              open={quoteDialogOpen}
              overrides={taxOverrides}
              onOverridesChange={(patch) => setTaxOverrides((prev) => ({ ...prev, ...patch }))}
              onDataChange={setPreflightSummary}
            />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="quote-issue-date" className="text-secondary-foreground">Issue Date *</Label>
                <Input
                  id="quote-issue-date"
                  type="date"
                  value={quoteForm.issue_date}
                  onChange={(e) => setQuoteForm({ ...quoteForm, issue_date: e.target.value })}
                  required
                  className="bg-input border-input text-foreground"
                  data-testid="quote-issue-date-input"
                />
              </div>
              <div>
                <Label htmlFor="quote-valid-until" className="text-secondary-foreground">Valid Until *</Label>
                <Input
                  id="quote-valid-until"
                  type="date"
                  value={quoteForm.valid_until}
                  onChange={(e) => setQuoteForm({ ...quoteForm, valid_until: e.target.value })}
                  required
                  className="bg-input border-input text-foreground"
                  data-testid="quote-valid-until-input"
                />
              </div>
            </div>

            {/* EF-QUOTE-TAX-FIELD-FORCE-REMOVAL-V2: Tax Rate field removed - backend uses tenant settings */}

            <div>
              <Label htmlFor="quote-notes" className="text-secondary-foreground">Notes</Label>
              <Textarea
                id="quote-notes"
                value={quoteForm.notes}
                onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                placeholder="Quote notes..."
                className="bg-input border-input text-foreground"
                rows={2}
                data-testid="quote-notes-input"
              />
            </div>

            {/* EVENTSFLO-PDF-VAT-LINE-LABEL-TOGGLE-V1
                Admin-facing toggle: whether the generated Quote PDF
                shows per-line VAT/TVA labels next to each line item.
                Default ON. Turning off keeps all totals, RC notes and
                internal tax audit snapshots intact — only the inline
                per-line badges are hidden. */}
            <div className="border border-border/60 bg-muted/30 rounded p-3">
              <label
                className="flex items-start gap-3 cursor-pointer select-none"
                htmlFor="quote-generate-show-line-tax-labels-toggle"
              >
                <input
                  id="quote-generate-show-line-tax-labels-toggle"
                  type="checkbox"
                  className="mt-1 h-4 w-4 accent-primary"
                  checked={quoteForm.show_line_tax_labels_on_pdf === true}
                  onChange={(e) =>
                    setQuoteForm({
                      ...quoteForm,
                      show_line_tax_labels_on_pdf: e.target.checked,
                    })
                  }
                  data-testid="quote-generate-show-line-tax-labels-toggle"
                />
                <span className="text-sm">
                  <span className="font-medium text-foreground">
                    Display VAT/tax label on PDF line items
                  </span>
                  <span className="block text-xs text-muted-foreground mt-0.5">
                    When enabled, PDF line items show labels such as
                    &ldquo;VAT 0% Non-EU&rdquo; or &ldquo;TVA 20%&rdquo;.
                    Tax totals and notes are always retained.
                  </span>
                </span>
              </label>
            </div>

            {/* EVENTSFLO-TAX-CLASS-DEFAULTS-AND-GENERATION-PREFLIGHT-V1
                Preflight guard: hard-block Generate Quote when the tax
                preflight surfaces an aggregator error, or when manual-
                review lines exist AND the admin has kept the safety
                toggle on. Turning off the safety toggle allows generation
                to proceed with a visible warning. */}
            {(() => {
              const preflightBlocked =
                !!preflightSummary.aggregator_error ||
                (preflightSummary.manual_review_count > 0 &&
                  taxOverrides.blockOnManualReview);
              const isAggBlock = !!preflightSummary.aggregator_error;
              const isMrBlock =
                !isAggBlock &&
                preflightSummary.manual_review_count > 0 &&
                taxOverrides.blockOnManualReview;
              const isMrWarn =
                preflightSummary.manual_review_count > 0 &&
                !taxOverrides.blockOnManualReview;
              return (
                <>
                  {isAggBlock && (
                    <div
                      className="border border-red-500/40 bg-red-500/10 text-xs rounded p-2"
                      data-testid="quote-generate-block-aggregator"
                    >
                      <strong className="text-red-500">Blocked:</strong>{" "}
                      Tax preflight could not be calculated safely. Please
                      resolve this before generating the quote.
                    </div>
                  )}
                  {isMrBlock && (
                    <div
                      className="border border-amber-500/40 bg-amber-500/10 text-xs rounded p-2"
                      data-testid="quote-generate-block-manual-review"
                    >
                      <strong className="text-amber-500">Blocked:</strong>{" "}
                      Manual-review tax lines exist ({preflightSummary.manual_review_count}). Review or adjust
                      these lines before generating, or turn off the block
                      only if authorised.
                    </div>
                  )}
                  {isMrWarn && (
                    <div
                      className="border border-amber-500/40 bg-amber-500/10 text-xs rounded p-2"
                      data-testid="quote-generate-warn-manual-review"
                    >
                      <strong className="text-amber-500">Warning:</strong>{" "}
                      Manual-review safety block is OFF and{" "}
                      {preflightSummary.manual_review_count} line
                      {preflightSummary.manual_review_count === 1 ? "" : "s"}{" "}
                      still require manual review. Provide an override
                      reason in the Tax Policy Review section before
                      generating.
                    </div>
                  )}
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setQuoteDialogOpen(false)}
                      className="border-border text-secondary-foreground"
                      disabled={quoteLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                      disabled={
                        quoteLoading ||
                        shifts.length === 0 ||
                        preflightBlocked ||
                        (isMrWarn && !(taxOverrides.overrideReason || "").trim())
                      }
                      data-testid="submit-quote-btn"
                    >
                      {quoteLoading ? "Generating..." : "Generate Quote"}
                    </Button>
                  </DialogFooter>
                </>
              );
            })()}
          </form>
        </DialogContent>
      </Dialog>

      {/* EVENTSFLO-CREW-PASSPORT-ID-DETAILS-OPTIONAL-TAB-AND-SCOPED-EXPORT-V1 */}
      <AlertDialog
        open={passportExportConfirmOpen}
        onOpenChange={setPassportExportConfirmOpen}
      >
        <AlertDialogContent className="bg-card border-border max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <BookUser className="h-5 w-5 text-primary" />
              Export Passport / ID Details
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              This export contains sensitive crew identity information.
              Only share it with authorised recipients (yacht agents,
              venues, or security teams who have requested crew ID
              details for this project).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div
            className="text-xs text-muted-foreground border border-amber-500/30 bg-amber-500/5 rounded-md p-3"
            data-testid="export-passport-id-scope-note"
          >
            Includes only crew currently assigned to shifts in this
            project. Missing / partial passport rows are still listed
            so you can identify gaps before sharing.
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="export-passport-id-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExportPassportIdDetails}
              disabled={passportExportLoading}
              data-testid="export-passport-id-confirm"
            >
              {passportExportLoading ? "Preparing…" : "Download .xlsx"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Shift Confirmation */}
      {/* Governed Deletion Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={(open) => {
        setDeleteDialogOpen(open);
        if (!open) {
          setDeletionImpact(null);
          setDeletionReason("");
        }
      }}>
        <AlertDialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-destructive" />
              Delete Shift
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground">
              Are you sure you want to delete &quot;{selectedShift?.name}&quot;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Impact Analysis - Shows while loading too */}
          <div className="my-4 p-4 bg-secondary/50 rounded-lg border border-border space-y-3">
            <div className="text-sm font-medium text-warning flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Deletion Impact
            </div>
            
            {!deletionImpact ? (
              <p className="text-sm text-muted-foreground">Analyzing dependencies...</p>
            ) : deletionImpact.error ? (
              <p className="text-sm text-muted-foreground">{deletionImpact.warning_message}</p>
            ) : deletionImpact.has_dependencies ? (
              <div className="text-sm text-secondary-foreground space-y-2">
                <p>{deletionImpact.warning_message}</p>
                
                <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                  {deletionImpact.total_tasks > 0 && (
                    <div className="bg-surface-elevated/50 px-2 py-1 rounded">
                      <span className="text-muted-foreground">Tasks: </span>
                      <span className="text-foreground">{deletionImpact.total_tasks}</span>
                      {deletionImpact.rule_added_tasks > 0 && (
                        <span className="text-primary ml-1">({deletionImpact.rule_added_tasks} rule-added)</span>
                      )}
                    </div>
                  )}
                  {deletionImpact.assignment_count > 0 && (
                    <div className="bg-surface-elevated/50 px-2 py-1 rounded">
                      <span className="text-muted-foreground">Assignments: </span>
                      <span className="text-foreground">{deletionImpact.assignment_count}</span>
                    </div>
                  )}
                  {deletionImpact.financial_impact > 0 && (
                    <div className="bg-surface-elevated/50 px-2 py-1 rounded">
                      <span className="text-muted-foreground">Value: </span>
                      <span className="text-foreground">{formatCurrency(deletionImpact.financial_impact)}</span>
                    </div>
                  )}
                  {(deletionImpact.quotes_affected > 0 || deletionImpact.invoices_affected > 0) && (
                    <div className="bg-surface-elevated/50 px-2 py-1 rounded col-span-2">
                      <span className="text-warning">Referenced in: </span>
                      {deletionImpact.quotes_affected > 0 && <span className="text-foreground">{deletionImpact.quotes_affected} quote(s)</span>}
                      {deletionImpact.quotes_affected > 0 && deletionImpact.invoices_affected > 0 && <span className="text-muted-foreground">, </span>}
                      {deletionImpact.invoices_affected > 0 && <span className="text-foreground">{deletionImpact.invoices_affected} invoice(s)</span>}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">This shift has no dependencies.</p>
            )}
          </div>
          
          {/* Deletion Reason (Required) */}
          <div className="space-y-2">
            <Label className="text-secondary-foreground text-sm">
              Reason for deletion <span className="text-destructive">*</span>
            </Label>
            <Textarea
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              placeholder="Please provide a reason for deleting this shift (minimum 10 characters)..."
              className="bg-input border-input text-foreground placeholder:text-muted-foreground min-h-[80px]"
            />
            {(deletionReason?.trim() || "").length > 0 && (deletionReason?.trim() || "").length < 10 && (
              <p className="text-xs text-warning">{10 - (deletionReason?.trim() || "").length} more characters required</p>
            )}
          </div>
          
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel className="border-border text-secondary-foreground hover:bg-accent">
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleDeleteShift}
              disabled={deletionLoading || (deletionReason?.trim() || "").length < 10}
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground disabled:opacity-50"
              data-testid="confirm-delete-shift-btn"
            >
              {deletionLoading ? (
                <>
                  <span className="animate-spin mr-2">⟳</span>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Shift
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rule Prompt Dialog */}
      <RulePromptDialog
        open={rulePromptOpen}
        onOpenChange={setRulePromptOpen}
        entityType="shift"
        entity={pendingShift}
        rulePrompts={rulePrompts}
        onAcceptAll={handleRuleAcceptAll}
        onOverride={handleRuleOverride}
        onCancel={handleRuleCancel}
        loading={ruleLoading}
      />

      {/* Rule Resolution Modal (Mandatory compliance for triggered rules) */}
      <RuleResolutionModal
        isOpen={resolutionModalOpen}
        onClose={() => {
          // EF-RULE-PREVIEW-SPLIT: Cancel clears pending data - no shift created
          setResolutionModalOpen(false);
          setPendingShiftData(null);
          setPendingKeepOpen(false);
          setFiredRules([]);
          loadData();
        }}
        rules={firedRules}
        shiftId={resolutionShiftId}
        shiftData={resolutionShiftData}
        onSuccess={handleResolutionModalSuccess}
      />

      {/* Create Crew Chief Shift Dialog */}
      <AlertDialog open={crewLeaderDialogOpen} onOpenChange={setCrewLeaderDialogOpen}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Create Crew Chief Shift?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground space-y-3">
              <p>
                This shift requires a Crew Leader (4+ staff). Would you like to create a separate 
                shift for the Crew Chief at the same time?
              </p>
              {crewLeaderShiftData && (
                <div className="bg-secondary rounded-lg p-3 space-y-2 text-sm">
                  <p className="text-foreground font-medium">{crewLeaderShiftData.name}</p>
                  <p className="text-muted-foreground">
                    {crewLeaderShiftData.date} • {crewLeaderShiftData.start_time} - {crewLeaderShiftData.end_time}
                  </p>
                  <p className="text-muted-foreground">Crew: 1 (Crew Chief)</p>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                You can edit this shift afterwards to assign the specific crew chief.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={handleSkipCrewLeaderShift}
              className="border-border text-secondary-foreground hover:bg-accent"
            >
              Skip
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreateCrewLeaderShift}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="create-team-leader-shift-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Create Crew Chief Shift
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* EF-TRAVEL-ADVISOR-01: Travel Advisor Modal */}
      <TravelAdvisorModal
        open={travelAdvisorOpen}
        onOpenChange={setTravelAdvisorOpen}
        shiftDate={shiftForm.date}
        shiftStartTime={shiftForm.start_time}
        venueAddress={getVenueAddressForAdvisor()}
        crewList={crewList}
        onApplyValues={handleApplyTravelAdvisory}
      />

      {/* EF-DUPLICATION-PRECREATE-UX-ADJUSTMENT v1.1: Pre-create Duplication Modal */}
      {duplicationModalOpen && project && (
        <ShiftDuplicationModal
          isOpen={duplicationModalOpen}
          onClose={handleDuplicationClose}
          projectStartDate={project.start_date}
          projectEndDate={project.end_date}
          onConfirm={handleDuplicationConfirm}
          onSingleShift={handleDuplicationSingleShift}
          isLoading={duplicationLoading}
        />
      )}

      {/* EVENTSFLO-SHIFT-FORM-RULE-SUPPLEMENT-SAVE-INTEGRITY-AUDIT-FIX-V1 */}
      <AlertDialog
        open={discardConfirmOpen}
        onOpenChange={(o) => {
          if (!o && !discardInProgress) setDiscardConfirmOpen(false);
        }}
      >
        <AlertDialogContent
          className="bg-card border-white/10 max-w-md"
          data-testid="shift-discard-confirm-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-xs leading-relaxed">
              You changed{" "}
              <strong>
                {shiftLineItemsRef.current?.pendingChangeCount?.() ?? 0}
              </strong>{" "}
              supplement/line-item{" "}
              {((shiftLineItemsRef.current?.pendingChangeCount?.() ?? 0) === 1
                ? "row"
                : "rows")}{" "}
              in this session. Closing without saving will restore the original
              supplements exactly as they were before you opened this shift.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={discardInProgress}
              data-testid="shift-discard-cancel-btn"
            >
              Continue editing
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={discardInProgress}
              onClick={async (e) => {
                e.preventDefault();
                if (!shiftLineItemsRef.current?.discard) {
                  setDiscardConfirmOpen(false);
                  setShiftDialogOpen(false);
                  setEditingShift(null);
                  setShiftTasks([]);
                  setRuleAddedSupplements([]);
                  return;
                }
                setDiscardInProgress(true);
                try {
                  const { restored, failures } =
                    await shiftLineItemsRef.current.discard();
                  if (failures > 0) {
                    toast.error(
                      `Restored ${restored} change(s) — ${failures} could not be reverted automatically.`,
                    );
                  } else {
                    toast.success(
                      `Discarded ${restored} unsaved change(s).`,
                    );
                  }
                  await loadData();
                } catch (err) {
                  toast.error("Could not restore original supplements.");
                } finally {
                  setDiscardInProgress(false);
                  setDiscardConfirmOpen(false);
                  setShiftDialogOpen(false);
                  setEditingShift(null);
                  setShiftTasks([]);
                  setRuleAddedSupplements([]);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="shift-discard-confirm-btn"
            >
              {discardInProgress ? "Restoring..." : "Discard & restore"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
